import axios from 'axios';
import { createClient } from '@supabase/supabase-js';
import { config, isConfigured } from '../config';
import { shouldSendActingFor } from './delegationPaths';

// Create axios instance with default config
const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Session helpers — "Remember Me" controls whether the session id
// is persisted across browser restarts (localStorage) or only kept
// for the current tab session (sessionStorage).
export const sessionStore = {
  get() {
    return sessionStorage.getItem('sessionId') || localStorage.getItem('sessionId') || null;
  },
  set(sessionId, { rememberMe = true } = {}) {
    if (rememberMe) {
      localStorage.setItem('sessionId', sessionId);
      sessionStorage.removeItem('sessionId');
    } else {
      sessionStorage.setItem('sessionId', sessionId);
      localStorage.removeItem('sessionId');
    }
  },
  clear() {
    localStorage.removeItem('sessionId');
    sessionStorage.removeItem('sessionId');
  },
};

// Add request interceptor to include session ID
/**
 * Vekalet bağlamı — owner bir üye adına çalışırken doldurulur (PLAN §K7).
 *
 * Başlık HER isteğe DEĞİL, yalnız vekalet görevinin uçlarına eklenir (delegationPaths.js).
 * Global göndermek "güvenli" görünüyordu (sunucu varsayılan-red) ama pratikte vekaletteyken
 * uygulamanın kalanını 403'lerle kırıyordu — /hesabim "grubun yok" sanıyordu.
 */
const ACTING_FOR_STORAGE_KEY = 'viza_acting_for';

// Sekme-ömürlü kalıcılık: bağlam bellek değişkeninde kalsaydı sayfa yenilemede ve sayfalar
// arası geçişte düşerdi — owner'ın üye adına belge yükleyebileceği hiçbir ekran kalmıyordu.
// localStorage BİLEREK değil: ikinci sekme kendi bağlamını ayrı kurabilmeli, iki sekmenin
// birbirinin vekaletini ezmesi sessiz veri karışıklığı olurdu.
const readActingFor = () => {
  try {
    const raw = sessionStorage.getItem(ACTING_FOR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

let actingFor = readActingFor();   // { userId, name } | null
const delegationListeners = new Set();
const notifyDelegation = () => delegationListeners.forEach((listener) => listener());

export const delegation = {
  set: (userId, name = '', countrySlug = '') => {
    // countrySlug: üyenin başvuru ülkesi (üye satırından gelir). Vekaletli belge ekranı
    // ülkeyi bundan SABİT alır; serbest ülke seçici yok. sessionStorage'da durur (reload'a dayanıklı).
    actingFor = userId ? { userId, name, countrySlug: countrySlug || null } : null;
    if (actingFor) sessionStorage.setItem(ACTING_FOR_STORAGE_KEY, JSON.stringify(actingFor));
    else sessionStorage.removeItem(ACTING_FOR_STORAGE_KEY);
    notifyDelegation();
  },
  clear: () => {
    actingFor = null;
    sessionStorage.removeItem(ACTING_FOR_STORAGE_KEY);
    notifyDelegation();
  },
  current: () => actingFor?.userId ?? null,
  /** Bant için: { userId, name } | null. Referans değişene kadar sabit (useSyncExternalStore). */
  info: () => actingFor,
  subscribe: (listener) => {
    delegationListeners.add(listener);
    return () => delegationListeners.delete(listener);
  },
};

api.interceptors.request.use((config) => {
  const sessionId = sessionStore.get();
  if (sessionId) {
    config.headers['X-Session-ID'] = sessionId;
  }
  const actingForUserId = delegation.current();
  if (actingForUserId && shouldSendActingFor(config.url)) {
    config.headers['X-Acting-For'] = actingForUserId;
  }
  return config;
});

// Add response interceptor to handle 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const requestUrl = error.config?.url || '';

      // Let password change handle its own session expiry (show message before redirect)
      if (requestUrl.includes('/api/auth/change-password')) {
        return Promise.reject(error);
      }

      // Clear all session data
      sessionStore.clear();
      // Vekalet bağlamı OTURUMA bağlı: burada temizlenmezse aynı sekmedeki bir sonraki giriş
      // bayat "X adına" bandı ve önceki kullanıcının üye id'siyle atılmış 403'lerle açılırdı.
      delegation.clear();
      localStorage.removeItem('currentUser');
      localStorage.removeItem('hasCompletedPayment');

      // Redirect to login page if not already there
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        console.warn('🔒 Session expired or invalid, redirecting to login...');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Supabase Configuration (anon key only)
const supabase = isConfigured.supabase
  ? createClient(config.supabaseUrl, config.supabaseAnonKey)
  : null;

if (!isConfigured.supabase) {
  console.error('❌ Missing Supabase configuration. Please check your .env file.');
  console.error('   Required: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY');
}

// Backend Configuration
const BACKEND_URL = config.backendUrl;

// All AI requests go through backend for proper context enrichment

// Configurable user credentials from environment
const getUserCredentials = () => {
  const envUsername = import.meta.env.VITE_USER_USERNAME;
  const envPassword = import.meta.env.VITE_USER_PASSWORD;
  const envEmail = import.meta.env.VITE_USER_EMAIL || 'user@getviza.ai';
  const envRole = import.meta.env.VITE_USER_ROLE || 'user';

  // If environment credentials are configured, use them
  if (envUsername && envPassword && envUsername !== 'your_username') {
    return [
      {
        id: 1,
        username: envUsername,
        password: envPassword,
        role: envRole,
        email: envEmail
      }
    ];
  }

  // Fallback to default test credentials
  return [
    { id: 1, username: 'admin', password: 'admin123', role: 'admin', email: 'admin@getviza.ai' },
    { id: 2, username: 'test', password: 'test123', role: 'user', email: 'test@getviza.ai' },
    { id: 3, username: 'user', password: 'user123', role: 'user', email: 'user@getviza.ai' }
  ];
};

// Mock authentication functions
const mockAuth = {
  login: async (username, password) => {
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay

    const mockUsers = getUserCredentials();
    const user = mockUsers.find(u => u.username === username && u.password === password);
    if (!user) {
      throw new Error('Invalid credentials');
    }

    const sessionId = `session_${Date.now()}_${Math.random().toString(36)}`;
    const userData = {
      UserID: user.id,
      Username: user.username,
      Email: user.email,
      Role: user.role,
      IsActive: true
    };

    // Store session data
    localStorage.setItem('sessionId', sessionId);
    localStorage.setItem('currentUser', JSON.stringify(userData));
    localStorage.setItem('hasCompletedPayment', 'true'); // Mock payment completion
    return {
      success: true,
      user: userData,
      sessionId: sessionId,
      message: 'Login successful'
    };
  },

  logout: async () => {
    await new Promise(resolve => setTimeout(resolve, 200));
    localStorage.removeItem('sessionId');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('hasCompletedPayment');
    return { success: true, message: 'Logged out successfully' };
  },

  verifySession: async () => {
    await new Promise(resolve => setTimeout(resolve, 200));
    const sessionId = sessionStore.get();
    const currentUser = localStorage.getItem('currentUser');

    if (!sessionId || !currentUser) {
      throw new Error('No valid session');
    }

    return {
      success: true,
      user: JSON.parse(currentUser),
      sessionId: sessionId
    };
  }
};

// Auth API - Supabase Authentication
// ── V2 backend response adapters ──────────────────────────────────────────────
// The C# backend wraps every response in ApiResponse { data, success, error } with camelCase
// fields; the frontend (ported from the legacy Node backend) expects flat shapes. Adapt here.

// Auth payload → legacy flat/PascalCase the pages + AppContext read (user.UserID, sessionId, success).
function adaptAuthResult(body) {
  if (!body || body.success !== true || !body.data) {
    throw new Error(body?.error || 'Request failed');
  }
  const d = body.data;
  return {
    success: true,
    sessionId: d.sessionId,
    user: {
      UserID: d.userId,
      Username: d.username,
      Email: d.email,
      // Normalize role case: legacy backend returned lowercase ('admin'), the V2 .NET backend
      // serializes the UserRole enum as PascalCase ('Admin'). The frontend compares against
      // lowercase (e.g. Sidebar's `Role === 'admin'`), so normalize here for both backends.
      Role: d.role?.toLowerCase(),
      IsActive: d.isActive,
      RequirePasswordChange: d.requirePasswordChange,
    },
  };
}

// Data payload → for an object payload, hoist its fields up + keep `success` (callers read either);
// for an array/primitive payload, return it as-is. Throws on success:false.
function dataOf(response) {
  const body = response?.data;
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    if (body.success === false) throw new Error(body.error || 'Request failed');
    const d = body.data;
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      return { success: true, ...d };
    }
    return d;
  }
  return body;
}

export const authAPI = {
  login: async (username, password, options = {}) => {
    const { rememberMe = false } = options;
    try {
      // Use backend for authentication to create proper session
      const response = await api.post('/api/auth/login', {
        username,
        password,
        rememberMe,
      });

      const result = adaptAuthResult(response.data);
      if (result.sessionId) {
        sessionStore.set(result.sessionId, { rememberMe });
      }
      return result;

    } catch (error) {
      console.error('Backend login failed:', error);

      // If backend is unavailable, throw error
      throw error;
    }
  },

  // --- Social sign-in (step 1) ---
  // Exchanges a provider credential for a session. When the provider identity has no account yet
  // the backend answers 200 with { requiresRegistration: true } and writes NOTHING to the database
  // — so this must be checked BEFORE adaptAuthResult, which throws when sessionId is absent.
  //
  // The backend deliberately never answers 401 here: the response interceptor above clears the
  // stored session on ANY 401, which would log the user out of an existing tab.
  oauthSignIn: async (provider, credential, options = {}) => {
    const { rememberMe = true } = options;
    const response = await api.post(`/api/auth/oauth/${provider}`, { credential });

    const body = response.data;
    if (!body || body.success !== true || !body.data) {
      throw new Error(body?.error || 'Request failed');
    }

    const d = body.data;
    if (d.requiresRegistration) {
      return {
        requiresRegistration: true,
        prefill: { email: d.email, firstName: d.firstName, lastName: d.lastName },
      };
    }

    // Re-wrap into the envelope adaptAuthResult expects so social and password logins produce
    // byte-identical shapes downstream — AppContext and the pages stay unaware of the difference.
    const result = adaptAuthResult({ success: true, data: d.user });
    if (result.sessionId) {
      sessionStore.set(result.sessionId, { rememberMe });
    }
    return { ...result, requiresRegistration: false, accountLinked: Boolean(d.accountLinked) };
  },

  // --- Social sign-in (step 2) ---
  // Completes signup for a provider identity. The same credential is re-validated server-side, so
  // email and name still come from a provider-signed source rather than from this client.
  oauthRegister: async (provider, credential, phone, kvkkAccepted, options = {}) => {
    const { rememberMe = true, contactConsent = false } = options;
    const response = await api.post(`/api/auth/oauth/${provider}/register`, {
      credential,
      phone,
      kvkkAccepted,
      contactConsent,
    });

    const result = adaptAuthResult(response.data);
    if (result.sessionId) {
      sessionStore.set(result.sessionId, { rememberMe });
    }
    return result;
  },

  register: async (firstName, lastName, email, password, phone, contactConsent = false) => {
    try {
      const response = await api.post('/api/auth/register', {
        firstName,
        lastName,
        email,
        password,
        phone,
        contactConsent,
      });
      return response.data;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  },

  // Magic link verification
  verifyMagicLink: async (token) => {
    try {
      const response = await api.get(`/api/auth/verify-magic-link?token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Magic link verification failed:', error);
      throw error;
    }
  },

  resendMagicLink: async (email) => {
    try {
      const response = await api.post('/api/auth/resend-magic-link', { email });
      return response.data;
    } catch (error) {
      console.error('Resend magic link failed:', error);
      throw error;
    }
  },

  // Password reset endpoints
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Forgot password failed:', error);
      throw error;
    }
  },

  // Token-based password reset (Task 10)
  verifyResetToken: async (token) => {
    try {
      const response = await api.post('/api/auth/reset-password/verify-token', { token });
      return response.data;
    } catch (error) {
      console.error('Verify reset token failed:', error);
      throw error;
    }
  },

  resetPassword: async (token, newPassword) => {
    try {
      const response = await api.post('/api/auth/reset-password', { token, newPassword });
      return response.data;
    } catch (error) {
      console.error('Reset password failed:', error);
      throw error;
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Change password failed:', error);
      throw error;
    }
  },

  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
      // Don't throw - we still want to clear local storage
    } finally {
      // Always clear session storage
      sessionStore.clear();
      // Vekalet bağlamı OTURUMA bağlı: burada temizlenmezse aynı sekmedeki bir sonraki giriş
      // bayat "X adına" bandı ve önceki kullanıcının üye id'siyle atılmış 403'lerle açılırdı.
      delegation.clear();
      localStorage.removeItem('currentUser');
      localStorage.removeItem('hasCompletedPayment');
    }
    return { success: true, message: 'Logged out successfully' };
  },

  verifySession: async () => {
    // Restores the session on app load; throws on invalid/expired → AppContext sends to /login.
    const response = await api.get('/api/auth/verify-session');
    return adaptAuthResult(response.data);
  }
};

// Chat API with Python Web App (Primary) and Emergency Fallback
export const chatAPI = {
  // T16: pre-warm chat-api's per-country KB cache (T15's POST /api/chat/warmup) as soon as a
  // country is known, so the user's first real chat question for it skips the live Supabase
  // round-trip. Deliberately fire-and-forget from the caller's perspective — never throws, and
  // any failure (network, auth, backend error) is swallowed here and only logged, exactly like
  // the other best-effort chat/* fetches below (getHistory, getConversations): a warmup miss
  // must never surface to the user or block rendering/navigation.
  warmupKnowledgeBase: async (countrySlug) => {
    try {
      await api.post('/api/chat/warmup', { country_slug: countrySlug });
    } catch (error) {
      console.error('warmupKnowledgeBase failed (non-fatal, ignored):', error);
    }
  },

  sendMessage: async (message, conversationId = null, language = 'tr', selectedCountry = null, onChunk = null, conversationHistory = []) => {
    const lastRequestTime = localStorage.getItem('lastChatRequest');
    const now = Date.now();
    if (lastRequestTime && (now - parseInt(lastRequestTime)) < 1000) {
      await new Promise(resolve => setTimeout(resolve, 1000 - (now - parseInt(lastRequestTime))));
    }
    localStorage.setItem('lastChatRequest', now.toString());

    // Get session ID for authentication
    const sessionId = sessionStore.get();

    if (!sessionId) {
      throw new Error('Please login first');
    }

    try {



      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionId
        },
        body: JSON.stringify({
          message: message,
          sessionId: conversationId || 'default',
          // Real chat_conversations.id (thread) — sent alongside the legacy sessionId field
          // above (left as-is, unrelated/out of scope). When provided, the backend targets
          // this specific thread instead of "resume most recent for this country".
          conversationId: conversationId,
          language: language,
          selectedCountry: selectedCountry,
          conversationHistory: conversationHistory,  // prior turns → AI context (continuity across reload)
          stream: !!onChunk  // Enable streaming if callback provided
        }),
      });

      // Handle rate limit with helpful message
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After') || '60';
        throw new Error(`I'm experiencing high traffic right now. Please wait about ${retryAfter} seconds and try again. Thank you for your patience! 🙏`);
      }

      if (!response.ok) {
        let errorText = '';
        try {
          const errorData = await response.json();
          if (response.status === 403 && errorData.error === 'FREE_CHAT_LIMIT_REACHED') {
            const err = new Error(errorData.message || 'Ücretsiz mesaj hakkınız doldu.');
            err.code = 'FREE_CHAT_LIMIT_REACHED';
            throw err;
          }
          errorText = JSON.stringify(errorData);
        } catch (parseErr) {
          if (parseErr.code === 'FREE_CHAT_LIMIT_REACHED') throw parseErr;
          try { errorText = await response.text(); } catch { errorText = 'Unknown error'; }
        }

        console.error(`chat-api error: ${response.status} - ${errorText}`);

        if (response.status >= 500) {
          throw new Error('Our AI service is temporarily undergoing maintenance. Please try again in a few minutes.');
        }

        throw new Error(`Service error: ${response.status}. Please try again later.`);
      }

      // Check if response is streaming (SSE)
      const contentType = response.headers.get('content-type');

      if (onChunk && contentType && contentType.includes('text/event-stream')) {
        // Streaming mode - read SSE events
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let fullResponse = '';
        let metadata = null;

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE messages (lines ending with \n\n)
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // Keep incomplete message in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const dataStr = line.substring(6); // Remove 'data: ' prefix

              try {
                const eventData = JSON.parse(dataStr);

                if (eventData.done) {
                  // Final message with metadata. conversationId is a TOP-LEVEL sibling of
                  // metadata in the backend's SSE payload (matches the non-streaming JSON
                  // response's shape, where it's also top-level) — fold it in here so the
                  // existing `metadata?.conversationId` read below picks it up.
                  metadata = eventData.metadata || {};
                  if (eventData.conversationId) {
                    metadata.conversationId = eventData.conversationId;
                  }
                  // T4: same top-level-sibling shape for the cover-letter-redirect
                  // short-circuit's `redirect` field (chat-api routers/chat.py's
                  // `_cover_letter_redirect_response`) — fold it in here too so callers read
                  // `response.redirect` the same way regardless of streaming/non-streaming.
                  if (eventData.redirect) {
                    metadata.redirect = eventData.redirect;
                  }

                  // If there's a response in the final message (tool calling mode),
                  // use it if we didn't receive any chunks during streaming
                  if (!fullResponse && (eventData.response || eventData.chunk)) {
                    // Prefer 'response' field, fallback to 'chunk'
                    const finalText = eventData.response || eventData.chunk || '';
                    if (finalText) {
                      fullResponse = finalText;
                      console.log('📝 Using response from final message (tool calling mode)');
                      // Call onChunk for UI update
                      if (onChunk) {
                        onChunk(finalText);
                      }
                    }
                  }

                  if (eventData.error) {
                    throw new Error(eventData.error);
                  }
                } else if (eventData.chunk) {
                  // Stream chunk to callback
                  fullResponse += eventData.chunk;
                  onChunk(eventData.chunk);
                }
              } catch (parseError) {
                console.error('Error parsing SSE data:', parseError);
              }
            }
          }
        }

        return {
          success: true,
          response: fullResponse || 'I received your message but couldn\'t generate a response. Please try again.',
          sessionId: metadata?.sessionId || conversationId || 'default',
          // Real chat_conversations.id (thread) for this turn — folded into `metadata` above
          // from the SSE event's top-level `conversationId` field (matches the non-streaming
          // JSON response, where it's also top-level). Do not read `sessionId` as a
          // substitute, it's an unrelated vestigial field.
          conversationId: metadata?.conversationId || null,
          mode: 'ai_service',
          service: 'chat-api',
          model: metadata?.model || null,
          from_cache: metadata?.from_cache || false,
          // Backend sends metadata.toolCalls; ChatPage reads response.toolCalls for cover-letter
          // auto-detect — preserve it on the streaming path (was dropped → always []).
          toolCalls: metadata?.toolCalls || [],
          // T4: top-level redirect target (e.g. "/cover-letter") when this turn short-circuited
          // to a canned assistant message instead of running the LLM. null on a normal turn.
          redirect: metadata?.redirect || null
        };
      } else {
        // Non-streaming mode (backward compatibility)
        const data = await response.json();

        // Handle rate limit errors from backend
        const responseText = typeof data.response === 'string' ? data.response : JSON.stringify(data.response);
        if (responseText && responseText.includes('Error code: 429')) {
          throw new Error('I\'m experiencing high traffic right now. Please wait about 60 seconds and try again. Thank you for your patience! 🙏');
        }

        return {
          success: true,
          response: data.ai_response || data.response || 'I received your message but couldn\'t generate a response. Please try again.',
          sessionId: data.sessionId || conversationId || 'default',
          // Real chat_conversations.id (thread) — already returned by the backend's
          // non-streaming JSON response today. Callers should read THIS, not `sessionId`
          // (that field is an unrelated vestigial echo of the request's sessionId).
          conversationId: data.conversationId || null,
          mode: 'ai_service',
          service: 'chat-api',
          model: data.model || null,
          from_cache: data.from_cache || false,
          freeChatLimitReached: data.freeChatLimitReached || false,
          // Parity with the streaming path so response.toolCalls is consistent for any caller.
          toolCalls: data.toolCalls || [],
          // T4: parity with the streaming path — see the SSE branch above for what this means.
          redirect: data.redirect || null
        };
      }

    } catch (error) {
      console.error('🌐 chat-api error:', error);

      // Re-throw user-friendly errors
      if (error.message.includes('high traffic') || error.message.includes('maintenance')) {
        throw error;
      }

      // Network or other errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error('Network connection error. Please check your internet connection and try again.');
      }

      // Generic fallback error
      throw new Error('I\'m having trouble connecting to the AI service. Please try again in a moment or contact support if the issue persists.');
    }

    // Her AI cagrisi gateway uzerinden (same-origin) AI servislerine gider.
  },

  sendMessageWithFile: async (formData) => {
    // Rate limiting: Simple delay between requests
    const lastRequestTime = localStorage.getItem('lastChatRequest');
    const now = Date.now();
    if (lastRequestTime && (now - parseInt(lastRequestTime)) < 1000) {
      await new Promise(resolve => setTimeout(resolve, 1000 - (now - parseInt(lastRequestTime))));
    }
    localStorage.setItem('lastChatRequest', now.toString());

    const sessionId = sessionStore.get();

    if (!sessionId) {
      throw new Error('Please login first');
    }

    try {
      // Create AbortController with extended timeout for passport analysis (2 minutes)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutes

      try {
        const response = await fetch(`${BACKEND_URL}/api/chat-with-passport`, {
          method: 'POST',
          headers: {
            'x-session-id': sessionId
            // Don't set Content-Type for FormData, let browser set it with boundary
          },
          body: formData,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Handle rate limit with helpful message
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After') || '60';
          throw new Error(`I'm experiencing high traffic right now. Please wait about ${retryAfter} seconds and try again. Thank you for your patience! 🙏`);
        }

        // Handle maintenance mode
        if (response.status === 503) {
          throw new Error('The AI service is temporarily under maintenance. Please try again in a few minutes. 🔧');
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.error('🌐 chat-with-passport HTTP error:', response.status, errorText);
          throw new Error(`AI service error (${response.status}). Please try again.`);
        }

        const data = await response.json();
        if (data.error) {
          throw new Error(data.error);
        }

        return {
          success: true,
          response: data.response || 'File uploaded and processed successfully.',
          sessionId: data.sessionId || 'default',
          mode: 'ai_service_with_file',
          service: 'chat-api',
          // Etiket alanlari: backend ne dediyse o. Sabit bir model adi yazmiyoruz — 'gpt-4o-mini'
          // burada yillardir yanlis duruyordu (stack'te OpenAI yok) ve teshiste yanlis yone cekiyor.
          model: data.model || null,
          hasFile: data.has_file || false
        };
      } catch (fetchError) {
        clearTimeout(timeoutId);

        // Handle AbortController timeout
        if (fetchError.name === 'AbortError') {
          throw new Error('Pasaport analizi çok uzun sürdü. Lütfen tekrar deneyin veya daha küçük bir dosya yükleyin.');
        }

        throw fetchError;
      }

    } catch (error) {
      console.error('🌐 chat-with-passport file upload error:', error);

      // Re-throw user-friendly errors
      if (error.message.includes('high traffic') || error.message.includes('maintenance')) {
        throw error;
      }

      // Network or other errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error('Network connection error. Please check your internet connection and try again.');
      }

      // Generic fallback error
      throw new Error('I\'m having trouble processing your file. Please try again in a moment or contact support if the issue persists.');
    }
  },

  downloadCoverLetterPDF: async (data) => {
    const sessionId = sessionStore.get();

    if (!sessionId) {
      throw new Error('Please login first');
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/cover-letter/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionId
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF generation error:', response.status, errorText);
        throw new Error(`PDF generation failed: ${response.status}`);
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('PDF download error:', error);
      throw error;
    }
  },

  // Task 3.3: New PDF generation endpoint that uses backend proxy
  generateCoverLetterPDF: async (coverLetterText, userInfo) => {
    try {
      const sessionId = sessionStore.get();
      if (!sessionId) {
        throw new Error('No active session. Please log in again.');
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/cover-letter/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId
        },
        body: JSON.stringify({
          coverLetterText: coverLetterText,
          userInfo: userInfo
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('PDF generation error:', response.status, errorData);
        throw new Error(errorData.error || `PDF generation failed: ${response.status}`);
      }

      // The Python cover-letter-api returns JSON, NOT a binary blob:
      //   local / no-GCS → { success, filename, pdf_data: <base64>, content_type }
      //   prod / GCS     → { success, filename, storage_path, download_url }
      // Decode base64 → Blob, or fetch the download_url. (Previously this read response.blob()
      // which wrapped the JSON text into a corrupt "PDF".)
      const data = await response.json();
      if (data.success === false) {
        throw new Error(data.error || 'PDF generation failed');
      }
      const filename = data.filename || 'cover_letter.pdf';
      let pdfBlob;
      if (data.pdf_data) {
        const bytes = Uint8Array.from(atob(data.pdf_data), (c) => c.charCodeAt(0));
        pdfBlob = new Blob([bytes], { type: data.content_type || 'application/pdf' });
      } else if (data.download_url) {
        pdfBlob = await fetch(data.download_url).then((r) => r.blob());
      } else {
        throw new Error('PDF response missing pdf_data/download_url');
      }
      return {
        success: true,
        blob: pdfBlob,
        filename: filename
      };

    } catch (error) {
      console.error('PDF generation error:', error);
      throw error;
    }
  },

  // Map raw {id, role, content, metadata, created_at} rows (shared shape returned by both
  // GET /api/chat/history and GET /api/chat/conversations/{id}/messages) to the
  // {content, sender, timestamp} shape ChatPage.jsx/ChatWidget.jsx render.
  _mapMessageRows: (rows) => (rows || []).map(m => ({
    content: m.content,
    sender: m.role === 'user' ? 'user' : 'bot',
    timestamp: m.created_at ? new Date(m.created_at) : new Date(),
  })),

  // Load persisted chat history for a country -> frontend message shape. Best-effort ([] on error).
  getHistory: async (selectedCountry) => {
    try {
      const response = await api.get('/api/chat/history', { params: { selectedCountry } });
      const msgs = chatAPI._mapMessageRows(response.data?.messages);
      // Surface the resolved conversation id (previously dropped here) as a non-enumerable
      // property on the returned array — existing callers that only use `msgs.length`/`.map`
      // (ChatWidget.jsx) are unaffected, while ChatPage.jsx's thread sidebar reads it to sync
      // the active-thread highlight to whichever conversation auto-restore landed on.
      Object.defineProperty(msgs, 'conversationId', {
        value: response.data?.conversationId || null,
        enumerable: false,
      });
      return msgs;
    } catch (error) {
      console.error('getHistory failed:', error);
      return [];
    }
  },

  // List the caller's chat threads (optionally scoped to a country), most-recently-updated first.
  getConversations: async (countrySlug) => {
    try {
      const response = await api.get('/api/chat/conversations', { params: { selectedCountry: countrySlug } });
      return response.data;
    } catch (error) {
      console.error('getConversations failed:', error);
      return { success: false, conversations: [] };
    }
  },

  // Create a fresh, empty chat thread for a country. Returns { success, conversationId } or
  // { success: false, error } (e.g. no country resolvable).
  createConversation: async (countrySlug) => {
    try {
      const response = await api.post('/api/chat/conversations/new', null, { params: { selectedCountry: countrySlug } });
      return response.data;
    } catch (error) {
      console.error('createConversation failed:', error);
      const message = error.response?.data?.error || error.message || 'Failed to create conversation';
      return { success: false, error: message };
    }
  },

  // Load a specific thread's messages -> frontend message shape (mirrors getHistory's mapping;
  // the backend returns the same {id, role, content, metadata, created_at} row shape for both).
  getConversationMessages: async (conversationId) => {
    try {
      const response = await api.get(`/api/chat/conversations/${conversationId}/messages`);
      return {
        success: response.data?.success !== false,
        messages: chatAPI._mapMessageRows(response.data?.messages),
      };
    } catch (error) {
      console.error('getConversationMessages failed:', error);
      return { success: false, messages: [] };
    }
  },

  getDocumentTips: async (data) => {
    const sessionId = sessionStore.get();

    if (!sessionId) {
      throw new Error('Please login first');
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/document-tips`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionId
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Document tips error:', response.status, errorText);
        throw new Error(`Document tips generation failed: ${response.status}`);
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Document tips error:', error);
      throw error;
    }
  },
};

// Documents API
export const documentsAPI = {
  upload: async (files) => {
    // V2 Documents API binds a SINGLE `IFormFile file` per request (not a `documents` array) and
    // returns { data:{documentId,documentName,fileUrl}, success }. Upload each file in turn, then
    // expose the legacy { success, documents:[...] } the page reads. A failing file (e.g. 409
    // duplicate) propagates as an axios error so the page's 409-retry handler still fires.
    const list = Array.from(files || []);
    const uploaded = [];
    for (const file of list) {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/api/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const body = response.data;
      if (body && body.success === false) throw new Error(body.error || 'Upload failed');
      uploaded.push(body?.data ?? body);
    }
    return { success: true, documents: uploaded };
  },

  getDocuments: async () => {
    const response = await api.get('/api/documents');
    // V2: { data:[{id,documentName,documentType,documentCategory,fileSize,fileUrl,uploadedAt}], success }.
    // DocumentsPage maps over the return AS AN ARRAY reading legacy keys (name/size/type); the picker
    // reads documentTypeName/originalName. Return the array, remapping each row to those keys.
    const body = response.data;
    const rows = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : []);
    return rows.map((c) => ({
      ...c,
      name: c.documentName ?? c.name,
      size: c.fileSize ?? c.size,
      type: c.documentType ?? c.type,
      documentTypeName: c.documentType ?? c.documentTypeName,
      originalName: c.documentName ?? c.originalName,
    }));
  },

  deleteDocument: async (documentId) => {
    const response = await api.delete(`/api/documents/${documentId}`);
    return response.data;
  },

  downloadDocument: async (documentId, documentName) => {
    const response = await api.get(`/api/documents/download/${documentId}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', documentName);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  getDocumentBlob: async (documentId) => {
    const response = await api.get(`/api/documents/view/${documentId}`, {
      responseType: 'blob',
    });
    return new Blob([response.data], { type: response.headers['content-type'] });
  },

  viewDocument: async (documentId) => {
    // Fetch document as blob with authentication, then open in new tab
    const response = await api.get(`/api/documents/view/${documentId}`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const url = window.URL.createObjectURL(blob);

    // Open in new tab
    const newWindow = window.open(url, '_blank');

    // Clean up the blob URL after a delay (give browser time to load)
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);

    return url;
  },

  // Link an existing document to a country (no re-upload, shared GCS object)
  linkExistingDocument: async (sourceDocumentId, countrySlug, documentTypeName) => {
    try {
      const response = await api.post('/api/documents/link-existing', {
        sourceDocumentId,
        countrySlug,
        documentTypeName,
      });
      // V2: { data:{documentId,documentName,fileUrl,...}, success } (camelCase — NO id/name).
      // ProfilePage reads result.document.{id,name} → alias id←documentId, name←documentName.
      const body = response.data;
      const d = body?.data;
      return { success: body?.success === true, document: d ? { ...d, id: d.documentId, name: d.documentName } : null };
    } catch (error) {
      console.error('❌ Error linking existing document:', error);
      throw error;
    }
  },

  // Upload a standard document with document type for renaming
  uploadStandardDocument: async (documentType, file, countrySlug) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    if (countrySlug) {
      formData.append('countrySlug', countrySlug);
    }

    try {
      const response = await api.post('/api/documents/upload-standard', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // V2: { data:{documentId,documentName,...}, success } (camelCase — NO id/name). Callers read
      // result.document.{id,name} (e.g. setPassportDocumentId(result.document.id)) → alias both.
      const body = response.data;
      const d = body?.data;
      return { success: body?.success === true, document: d ? { ...d, id: d.documentId, name: d.documentName } : null };
    } catch (error) {
      console.error(`❌ Error uploading standard document:`, error);
      throw error;
    }
  },

  // Analyze uploaded documents with AI (single batch)
  analyzeDocuments: async (documents, country, countryDisplayName) => {
    try {
      // Extended timeout for document analysis (5 minutes) - Gemini API has rate limits
      const response = await api.post('/api/documents/analyze', {
        documents,
        country
      }, {
        timeout: 300000 // 300 seconds (5 minutes)
      });

      if (response.data.success) {
        // Generate summary if not provided by backend (ensure consistent formatting)
        const avgScore = response.data.statistics?.totalScore / response.data.statistics?.total || 0;
        const displayName = countryDisplayName || country;
        response.data.analysis = generateBatchSummary(
          response.data.documentResults,
          response.data.overallStatus || 'good',
          response.data.overallScore || avgScore,
          displayName
        );
        response.data.summary = response.data.analysis;
      }

      return response.data;
    } catch (error) {
      // Handle timeout error with user-friendly message
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error(`❌ Document analysis timeout:`, error);
        throw new Error('Belge analizi zaman aşımına uğradı. Lütfen daha az belge ile tekrar deneyin.');
      }
      console.error(`❌ Error analyzing documents:`, error);
      throw error;
    }
  },

  // Fetch persisted analysis status (and cached results) for the current user+country
  getAnalysisStatus: async (countrySlug) => {
    try {
      const response = await api.get('/api/documents/analysis-status', {
        params: { countrySlug }
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching analysis status:', error);
      throw error;
    }
  },

  // Persist purpose/profession selection (no analysis triggered)
  // educationType is optional (D-type education flow); backend ignores null for C-type.
  // educationType: D-tipi eğitim akışı ('lisans'/'yuksek'/'erasmus'/'erasmusStaj'/'hazirlik'),
  // C-tipi'de null. stayDuration: 'ssu'/'slu' — yalnızca "Kalış / Program Süresi" alanını
  // toplayan eğitim ülkelerinde (şu an İspanya) dolu, diğer her yerde null.
  saveSelection: async (countrySlug, purpose, profession, educationType = null, stayDuration = null) => {
    try {
      const response = await api.post('/api/documents/save-selection', {
        countrySlug,
        purpose,
        profession,
        educationType,
        stayDuration,
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error saving selection:', error);
      throw error;
    }
  },

  // Analyze all documents via a single SSE connection.
  // Batching (BATCH_SIZE=2) is handled server-side; this function just streams the results.
  // Keepalive comments from the server reset Cloudflare's 100s idle timer automatically.
  analyzeDocumentsInBatches: async (documents, country, onProgress, countryDisplayName, extraParams = {}) => {
    const allResults = [];
    let totalSuccess = 0;
    let totalWarning = 0;
    let totalError = 0;
    let totalScore = 0;

    const sessionId = sessionStore.get() || '';
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';

    const headers = {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
    };
    // Bu çağrı SSE için axios yerine ham fetch kullanır — interceptor buraya uğramaz, vekalet
    // başlığı elle eklenir. Vekalette analiz ÜYE adına koşar (sunucu Full kapsam şartı koyar).
    const actingForUserId = delegation.current();
    if (actingForUserId) headers['X-Acting-For'] = actingForUserId;

    const response = await fetch(`${baseUrl}/api/documents/analyze`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ documents, country, ...extraParams })
    });

    if (!response.ok) {
      throw new Error(`Analiz isteği başarısız: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep last incomplete line

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue; // skip keepalive comments and blank lines
        try {
          const event = JSON.parse(line.slice(6));

          if (event.type === 'batch_start') {
            if (onProgress) onProgress({
              type: 'batch_start',
              batchNumber: event.batchNumber,
              totalBatches: event.totalBatches,
              documentsInBatch: event.documentsInBatch,
              totalDocuments: event.totalDocuments,
              processedDocuments: allResults.length,
              message: event.message
            });

          } else if (event.type === 'batch_complete') {
            const results = event.documentResults || [];
            allResults.push(...results);
            results.forEach(doc => { totalScore += doc.score || 0; });
            const stats = event.statistics || {};
            totalSuccess += stats.success || 0;
            totalWarning += stats.warning || 0;
            totalError += stats.error || 0;

            if (onProgress) onProgress({
              type: 'batch_complete',
              batchNumber: event.batchNumber,
              totalBatches: event.totalBatches,
              batchResults: results,
              processedDocuments: allResults.length,
              totalDocuments: event.totalDocuments,
              message: event.message
            });

          } else if (event.type === 'batch_error') {
            // `docEntry` is normally a plain documentType string, but tolerate a richer
            // { id, type } shape too so an id survives through if the event ever carries one.
            // A whole-BATCH failure is a TECHNICAL failure by definition (not a document-content
            // problem), so these synthesized rows are always tagged errorCategory:'transient' —
            // never 'genuine' — which is what makes them eligible for the bulk "Analiz
            // Edilemeyenleri Tekrar Dene" auto-retry and keeps them out of the red "Sorun" bucket
            // (see docanalysis-inline-status-ux / DocumentAnalysisStatusChip.getDocumentStatusInfo).
            (event.documents || []).forEach(docEntry => {
              const isObj = docEntry && typeof docEntry === 'object';
              const docType = isObj ? (docEntry.type ?? docEntry.documentType) : docEntry;
              const docId = isObj ? docEntry.id : undefined;
              allResults.push({
                id: docId != null ? String(docId) : undefined,
                documentType: docType,
                status: 'error',
                errorCategory: 'transient',
                feedback: 'Bu belge analiz edilemedi. Lütfen tekrar deneyin.',
                details: [],
                suggestions: ['Belgeyi tekrar yüklemeyi deneyin'],
                score: 0
              });
              totalError++;
            });
            if (onProgress) onProgress({
              type: 'batch_error',
              batchNumber: event.batchNumber,
              totalBatches: event.totalBatches,
              error: event.message,
              processedDocuments: allResults.length,
              totalDocuments: documents.length,
              message: event.message
            });

          } else if (event.type === 'error') {
            // Tamamlanma kapısı hatası 'message' alanında gelir ('error' değil) — yalnız
            // event.error okumak eksik-belge mesajını "undefined"a çeviriyordu.
            throw new Error(event.error || event.message || 'Analiz başlatılamadı');
          }
          // type === 'done': stream will end naturally
        } catch (parseErr) {
          if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
        }
      }
    }

    const avgScore = documents.length > 0 ? totalScore / documents.length : 0;
    let overallStatus = 'good';
    if (totalError > 0) overallStatus = 'needs_attention';
    else if (totalWarning > documents.length / 2) overallStatus = 'needs_improvement';

    const displayName = countryDisplayName || country;
    const summary = generateBatchSummary(allResults, overallStatus, avgScore, displayName);

    return {
      success: true,
      documentResults: allResults,
      summary,
      analysis: summary,
      overallStatus,
      overallScore: Math.round(avgScore * 10) / 10,
      statistics: {
        total: documents.length,
        success: totalSuccess,
        warning: totalWarning,
        error: totalError
      }
    };
  },
};

// Helper function to generate batch summary
function generateBatchSummary(results, overallStatus, avgScore, countryName) {
  const errorDocs = results.filter(r => r.status === 'error');
  const warningDocs = results.filter(r => r.status === 'warning');
  const successDocs = results.filter(r => r.status === 'success');

  // Format country name for display
  const displayCountry = formatCountryNameFallback(countryName);

  let summary = '';

  if (overallStatus === 'good') {
    summary = `## ✅ Belge Analizi Tamamlandı\n\n**${displayCountry}** vize başvurunuz için ${results.length} belge incelendi.\n\n**Genel Değerlendirme:** Belgeleriniz büyük ölçüde uygun görünüyor. (Puan: ${Math.round(avgScore)}/100)`;
  } else if (overallStatus === 'needs_improvement') {
    summary = `## ⚠️ Belge Analizi Tamamlandı\n\n**${displayCountry}** vize başvurunuz için ${results.length} belge incelendi.\n\n**Genel Değerlendirme:** Bazı belgelerinizde iyileştirmeler önerilmektedir. (Puan: ${Math.round(avgScore)}/100)`;
  } else {
    summary = `## ❌ Dikkat Edilmesi Gereken Belgeler Var\n\n**${displayCountry}** vize başvurunuz için ${results.length} belge incelendi.\n\n**Genel Değerlendirme:** Bazı belgeleriniz düzeltme gerektiriyor. (Puan: ${Math.round(avgScore)}/100)`;
  }

  summary += `\n\n### 📊 Özet\n- ✅ Başarılı: ${successDocs.length}\n- ⚠️ İyileştirme Önerilen: ${warningDocs.length}\n- ❌ Düzeltme Gereken: ${errorDocs.length}`;

  if (errorDocs.length > 0) {
    summary += `\n\n### ❌ Düzeltilmesi Gereken Belgeler\n`;
    errorDocs.forEach(doc => {
      // Show full feedback text, no truncation
      const feedback = doc.feedback || 'Detay için belgeye bakın';
      summary += `- **${doc.documentType}**: ${feedback}\n`;
    });
  }

  if (warningDocs.length > 0) {
    summary += `\n\n### ⚠️ İyileştirme Önerilen Belgeler\n`;
    warningDocs.forEach(doc => {
      const feedback = doc.feedback || 'Detay için belgeye bakın';
      summary += `- **${doc.documentType}**: ${feedback}\n`;
    });
  }

  return summary;
}

// Helper to format country name for display
function formatCountryNameFallback(name) {
  if (!name) return '';

  // If it's a slug (lowercase and has hyphens)
  if (name === name.toLowerCase() && (name.includes('-') || name.includes('_'))) {
    return name
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // If it's already capitalized or mixed case, return as is
  return name;
}

// Payment API
export const paymentAPI = {
  createPaymentIntent: async (amount, currency = 'USD') => {
    const response = await api.post('/api/payment/create-intent', { amount, currency });
    return response.data;
  },

  confirmPayment: async (paymentIntentId) => {
    const response = await api.post('/api/payment/confirm', { paymentIntentId });
    return response.data;
  },

  getPaymentStatus: async () => {
    const response = await api.get('/api/payment/status');
    return response.data;
  },

  recordPayment: async (paymentData) => {
    const response = await api.post('/api/payment/record', paymentData);
    return response.data;
  },

  checkPaymentHistory: async (country) => {
    const response = await api.get(`/api/payment/history?country=${encodeURIComponent(country)}`);
    return response.data;
  },

  // Get user payment history via backend proxy (hides token requests from browser)
  getUserPaymentHistoryViaBackend: async () => {
    try {
      const response = await api.get('/api/payment-service/user-history');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching payment history via backend:', error);
      throw error;
    }
  },

  // Get the user's appointment-notification package (1-month window) for the
  // day-countdown. Returns { active, status, startedAt, expiresAt, daysRemaining }.
  getNotificationPackage: async () => {
    try {
      const response = await api.get('/api/user/notification-package');
      // V2: { data: { active, status, startedAt, expiresAt, daysRemaining }, success };
      // callers read pkg.active / pkg.expiresAt etc. → dataOf hoists data up.
      return dataOf(response);
    } catch (error) {
      console.error('❌ Error fetching notification package:', error);
      throw error;
    }
  },

  // Force a back-fill from the .NET history then return the package. Called
  // right after a payment redirect so the countdown appears immediately.
  syncNotificationPackage: async () => {
    try {
      const response = await api.post('/api/user/notification-package/sync');
      // Same shape as getNotificationPackage → hoist data so callers read pkg.active / pkg.expiresAt.
      return dataOf(response);
    } catch (error) {
      console.error('❌ Error syncing notification package:', error);
      throw error;
    }
  },

  // --- hikie ödeme linki ---
  // Ödemeyi hikie tahsil eder. Akış: sözleşme onayı (acceptContracts) → initPaymentLink (Pending
  // kayıt + redirectUrl) → müşteri AYNI SEKMEDE hikie'ye gider → hikie /payment/success'e döner →
  // getPaymentLinkStatus ile onay beklenir (onay hikie'nin sunucu callback'iyle gelir).
  getPaymentLinkQuote: async ({ country, hasReservation = false }) => {
    const response = await api.get('/api/payment-service/payment-link/quote', { params: { country, hasReservation } });
    return response.data; // { available, amountTry, currency, country, hasReservation }
  },

  initPaymentLink: async ({ country, hasReservation = false, orderId }) => {
    const response = await api.post('/api/payment-service/payment-link/init', { country, hasReservation, orderId });
    return response.data; // { orderId, transactionId, paymentHistoryId, redirectUrl, amountTry }
  },

  getPaymentLinkStatus: async (orderId) => {
    const response = await api.get(`/api/payment-service/payment-link/status/${encodeURIComponent(orderId)}`);
    return response.data; // { merchantOrderId, status, isPaid, transactionId, paymentHistoryId }
  },

  // GEÇİCİ: hikie'nin order.paid webhook'u bizim sipariş numarasını taşımıyor. hikie'nin dönüş
  // URL'ine eklediği kendi sipariş numarası (orderNumber) ile imzalı ödeme bu siparişe bağlatılır;
  // bağlama kuralları (ürün, tutar, zaman, tek kullanım) sunucuda. 200 → ödendi, 202 → onay henüz
  // gelmedi (tekrar denenir), 4xx/503 → bağlanamaz (yalnız durum okunur).
  claimPaymentLink: async (orderId, orderNumber) => {
    const response = await api.post(`/api/payment-service/payment-link/claim/${encodeURIComponent(orderId)}`, { orderNumber });
    return response.data; // { merchantOrderId, status, isPaid, transactionId, paymentHistoryId }
  },

  // Admin "Ülke Atama" tab'ı: ülkesiz (unassigned) ödemeye ülke atar → belge-analizi erişimi açılır.
  // Admin: TEK bir kullanıcının ödeme geçmişi ("Ülke Değiştir" ekranı). /completed tüm ödemeleri
  // sayfalı döndüğü için tek kullanıcıyı orada aramak sayfa sayfa gezmek demekti.
  getUserPaymentHistoryForAdmin: async (userId) => {
    const response = await api.get(`/api/payment-service/admin/user-history/${userId}`);
    return response.data;
  },

  // Admin muafiyeti: ödeme alınmadan bir ülke için belge-analizi erişimi açar. Kayıt Checkout'un
  // kendi tablosunda tutulur ve user-history yanıtına AdminGrant satırı olarak eklenir (canlı ödeme
  // servisi ayrı repoda; oraya hiç yazılmaz). Ülke SLUG'ı gönderilir, görünen ad değil.
  // replaceCountry verilirse o ülkenin muafiyeti silinir — kullanıcıya iki ülkede bedava erişim kalmasın.
  grantPaymentAccess: async (userId, country, replaceCountry = null) => {
    const response = await api.post('/api/payment-service/admin/grant-access',
      replaceCountry ? { userId, country, replaceCountry } : { userId, country });
    return response.data;
  },

  // Kullanıcının mevcut muafiyetleri. AYRI uçtan okunur: admin/user-history düz proxy olduğu için
  // ödeme servisi okunamadığında "okunamadı" bilgisi bozulmaz, muafiyet bilgisi ise doğru gelir.
  getUserAccessGrants: async (userId) => {
    const response = await api.get(`/api/payment-service/admin/grants/${userId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  assignPaymentCountry: async (paymentHistoryId, country) => {
    const response = await api.patch(`/api/payment-service/${encodeURIComponent(paymentHistoryId)}/country`, { country });
    return response.data;
  },

  // Kullanıcı KENDİ atanmamış ödemesine ülke seçer (Başvurular ekranı).
  assignMyPaymentCountry: async (paymentHistoryId, country) => {
    const response = await api.patch(`/api/payment-service/my-payment/${encodeURIComponent(paymentHistoryId)}/country`, { country });
    return response.data;
  },

  // --- Mesafeli Satış Sözleşmesi + Ön Bilgilendirme Formu (Mesafeli Sözleşmeler Yönetmeliği) ---
  // Belgeler backend'de bu siparişin verileriyle render edilir; tutarlar sunucu-otoritatiftir.

  // Ödeme öncesi gösterim (kayıt oluşturmaz)
  previewContracts: async (payload) => {
    const response = await api.post('/api/payment-service/contracts/preview', payload);
    return response.data;
  },

  // "OKUDUM, ONAYLIYORUM" beyanını snapshot ile kalıcılaştırır — payment-link/init bunu şart koşar
  acceptContracts: async (payload) => {
    const response = await api.post('/api/payment-service/contracts/accept', payload);
    return response.data;
  },

  // Ödeme tamamlandıktan sonra sözleşmeyi kesinleştirir + nüshayı e-postalatır (idempotent)
  finalizeContracts: async (orderId, transactionId) => {
    const response = await api.post('/api/payment-service/contracts/finalize', { orderId, transactionId });
    return response.data;
  },

  // Kullanıcının sözleşme kayıtları (profil "Sözleşmelerim" için)
  getMyContracts: async () => {
    const response = await api.get('/api/payment-service/contracts/mine');
    return response.data;
  },

  // Tek sözleşme kaydının saklanan HTML nüshaları (/contracts/:id sayfası — e-postadaki linkler)
  getContract: async (id) => {
    const response = await api.get(`/api/payment-service/contracts/${encodeURIComponent(id)}`);
    return response.data;
  },

  // Get EUR/TRY exchange rate from TCMB via backend
  getExchangeRate: async () => {
    try {
      const response = await api.get('/api/exchange-rate');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching exchange rate:', error);
      throw error;
    }
  },

  // Coupon validation via payment service
  validateCoupon: async (code) => {
    try {
      const response = await api.get(`/api/payment-service/coupons/validate/${encodeURIComponent(code.trim())}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error validating coupon:', error);
      throw error;
    }
  },

  // Admin coupon management via payment service
  getCoupons: async () => {
    try {
      const response = await api.get('/api/payment-service/coupons');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching coupons:', error);
      throw error;
    }
  },

  createCoupon: async (couponData) => {
    try {
      const response = await api.post('/api/payment-service/coupons', couponData);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating coupon:', error);
      throw error;
    }
  },

  updateCoupon: async (id, couponData) => {
    try {
      const response = await api.put(`/api/payment-service/coupons/${id}`, couponData);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating coupon:', error);
      throw error;
    }
  },

  deleteCoupon: async (id) => {
    try {
      const response = await api.delete(`/api/payment-service/coupons/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting coupon:', error);
      throw error;
    }
  },

  // Bulk coupon campaign management via payment service
  getCouponCampaigns: async (companyName) => {
    try {
      const params = companyName ? `?companyName=${encodeURIComponent(companyName)}` : '';
      const response = await api.get(`/api/payment-service/coupon-campaigns${params}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching coupon campaigns:', error);
      throw error;
    }
  },

  getCouponCampaignCompanies: async () => {
    try {
      const response = await api.get('/api/payment-service/coupon-campaigns/companies');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching campaign companies:', error);
      throw error;
    }
  },

  createCouponCampaign: async (campaignData) => {
    try {
      const response = await api.post('/api/payment-service/coupon-campaigns', campaignData);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating coupon campaign:', error);
      throw error;
    }
  },

  exportCouponCampaign: async (id) => {
    try {
      const response = await api.get(`/api/payment-service/coupon-campaigns/${id}/export`, {
        responseType: 'blob',
      });

      const blob = response.data;
      if (blob.type === 'application/json') {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed.error || 'Excel dosyası indirilemedi');
      }

      return blob;
    } catch (error) {
      console.error('❌ Error exporting coupon campaign:', error);
      throw error;
    }
  },

  deactivateCouponCampaign: async (id) => {
    try {
      const response = await api.put(`/api/payment-service/coupon-campaigns/${id}/deactivate`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deactivating coupon campaign:', error);
      throw error;
    }
  },

  // Get completed payments for admin (with filtering and pagination)
  getCompletedPayments: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.pageNumber) params.append('pageNumber', filters.pageNumber.toString());
      if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

      const queryString = params.toString();
      const url = `/api/payment-service/completed${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching completed payments:', error);
      throw error;
    }
  },

  // Get failed payments for admin (with filtering and pagination)
  getFailedPayments: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.pageNumber) params.append('pageNumber', filters.pageNumber.toString());
      if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

      const queryString = params.toString();
      const url = `/api/payment-service/failed${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching failed payments:', error);
      throw error;
    }
  },

  addReservationToPayment: async (paymentId) => {
    try {
      const response = await api.patch(`/api/payment-service/${paymentId}/reservation`, {
        hasReservation: true
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error adding reservation to payment:', error);
      throw error;
    }
  },

  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionStore.clear();
      // Vekalet bağlamı OTURUMA bağlı: burada temizlenmezse aynı sekmedeki bir sonraki giriş
      // bayat "X adına" bandı ve önceki kullanıcının üye id'siyle atılmış 403'lerle açılırdı.
      delegation.clear();
      localStorage.removeItem('currentUser');
      localStorage.removeItem('hasCompletedPayment');
    }
    return { success: true, message: 'Logged out successfully' };
  },
};

// Profile API
export const profileAPI = {
  getProfile: async () => {
    const response = await api.get('/api/profile');
    // V2: { data: {firstName,lastName,country,city,profession,dateOfBirth,address,phone,profilePicture,...}, success }.
    // ProfilePage reads response.data.{FirstName,LastName,BirthCountry,BirthCity,Profession,BirthDate,
    // Address,Phone,ProfilePictureURL} — PascalCase, NESTED under data (so we keep the data wrapper).
    const body = response.data;
    const d = body?.data || {};
    return {
      success: body?.success === true,
      data: {
        FirstName: d.firstName,
        LastName: d.lastName,
        BirthCountry: d.country,
        BirthCity: d.city,
        Profession: d.profession,
        BirthDate: d.dateOfBirth,
        Address: d.address,
        Phone: d.phone,
        ProfilePictureURL: d.profilePicture,
      },
    };
  },

  updateProfile: async (profileData) => {
    // ProfilePage form uses {name,surname,birthCountry,birthCity,profession,birthDate,address,phone};
    // V2 UpdateProfileRequestDto expects {FirstName,LastName,Country,City,Profession,DateOfBirth,Address,Phone}.
    // Unset fields are preserved server-side (request.X ?? profile.X), so omitting passport/bio is safe.
    const payload = {
      FirstName: profileData.name,
      LastName: profileData.surname,
      Country: profileData.birthCountry,
      City: profileData.birthCity,
      Profession: profileData.profession,
      DateOfBirth: profileData.birthDate || null,
      Address: profileData.address,
      Phone: profileData.phone,
    };
    const response = await api.put('/api/profile', payload);
    return response.data;
  },

  // Codice fiscale (İtalyan vergi kodu). user_profiles.metadata'da KULLANICI bazında saklanır
  // (ülke bazında değil) — kod kişiye ait, bir kez girilir, İtalya başvurularının hepsinde
  // kullanılır. Biçim kontrolü istemcide (constants/codiceFiscale.js); "gerçekten kayıtlı mı"
  // sorusuna yalnızca Agenzia'nın resmî sayfası cevap verebiliyor, UI oraya link veriyor.
  getCodiceFiscale: async () => {
    try {
      const response = await api.get('/api/profile/codice-fiscale');
      // V2: { data: { value, savedAt } | null, success }. Kullanıcı hiç girmediyse data null.
      return response.data?.data || null;
    } catch (error) {
      console.error('❌ Error fetching codice fiscale:', error);
      return null; // Okuma hatası formu bloklamasın; kullanıcı yeniden yazabilir.
    }
  },

  // Boş değer göndermek kaydı SİLER (backend anahtarı kaldırır) — yanlış girilen kod
  // ayrı bir uç gerektirmeden geri alınabilsin diye.
  saveCodiceFiscale: async (value) => {
    const response = await api.put('/api/profile/codice-fiscale', { value: value ?? '' });
    return response.data;
  },

  uploadProfilePhoto: async (file) => {
    const formData = new FormData();
    // Alan adı 'file' olmak zorunda: V2 ucu `UploadPhoto(IFormFile file)` ve ASP.NET
    // IFormFile'ı parametre adına göre bağlıyor. Legacy Node backend 'photo' bekliyordu;
    // isim taşınmayınca istek backend'e ulaşıyor ama file null geliyor ve
    // "No file provided" dönüyordu.
    formData.append('file', file);

    const response = await api.post('/api/profile/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Hesap self-servisi: oturum yönetimi, onay geçmişi, KVKK veri dışa aktarımı.
// Hepsi Identity.Api'de /api/profile/* altında yaşıyor (gateway zaten oraya yönlendiriyor).
export const accountAPI = {
  getSessions: async () => {
    const response = await api.get('/api/profile/sessions');
    return response.data?.data ?? [];
  },

  revokeSession: async (sessionId) => {
    try {
      const response = await api.delete(`/api/profile/sessions/${sessionId}`);
      return { success: response.data?.success === true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Oturum kapatılamadı' };
    }
  },

  revokeOtherSessions: async () => {
    try {
      const response = await api.delete('/api/profile/sessions');
      return { success: response.data?.success === true, revoked: response.data?.data ?? 0 };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Oturumlar kapatılamadı' };
    }
  },

  getConsents: async () => {
    const response = await api.get('/api/profile/consents');
    return response.data?.data ?? [];
  },

  // KVKK md.11 erişim talebi. Sunucu JSON döndürür; indirmeyi burada tetikliyoruz.
  downloadExport: async () => {
    try {
      const response = await api.get('/api/profile/export');
      const payload = response.data?.data;
      if (!payload) return { success: false, error: 'Veriler alınamadı' };

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `viza-hesap-verilerim-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Veriler indirilemedi' };
    }
  },

  // --- KVKK veri saklama kararı (oturumlu yol) ---

  getRetentionStatus: async () => {
    try {
      const response = await api.get('/api/profile/retention');
      return response.data?.data ?? null;
    } catch {
      // Banner isteğe bağlı bir ek — durum çekilemezse sayfanın geri kalanı çalışmaya devam etsin.
      return null;
    }
  },

  submitRetentionDecision: async (decision) => {
    try {
      const response = await api.post('/api/profile/retention/decision', { decision });
      return { success: response.data?.success === true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Seçiminiz kaydedilemedi' };
    }
  },

  // --- Hesap silme ---

  requestDeletion: async (password) => {
    try {
      const response = await api.post('/api/profile/deletion/request', { password });
      return { success: response.data?.success === true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Talebiniz alınamadı' };
    }
  },

  cancelDeletion: async () => {
    try {
      const response = await api.post('/api/profile/deletion/cancel');
      return { success: response.data?.success === true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Talep iptal edilemedi' };
    }
  },
};

/**
 * E-postadaki KVKK karar bağlantısı — oturum gerektirmez, yetki token ile sağlanır.
 * GET yalnızca okur (posta tarayıcıları bağlantıları önden çeker); kayıt POST ile yapılır.
 */
/** Grup yönetimi — owner'ın üye listesi, üye ekleme, koltuk ve ilerleme. */
export const groupAPI = {
  myGroups: async () => {
    try {
      const response = await api.get('/api/groups');
      return { success: true, data: response.data?.data ?? [] };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Gruplar yüklenemedi', data: [] };
    }
  },

  create: async (name) => {
    try {
      const response = await api.post('/api/groups', { name });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Grup oluşturulamadı' };
    }
  },

  // Kullanıcının grup KURMA yetkisi — /grubum ve /hesabim boş durumları bununla karar verir.
  myGrant: async () => {
    try {
      const response = await api.get('/api/groups/my-grant');
      return { success: true, data: response.data?.data ?? { hasGrant: false, groupType: null } };
    } catch (error) {
      // Belirleyemiyorsak yetkisiz say: yanlışlıkla kurma formu göstermek, formu
      // gizlemekten daha kafa karıştırıcı (sunucu zaten 403 ile durdururdu).
      return { success: false, data: { hasGrant: false, groupType: null } };
    }
  },

  members: async (groupId) => {
    try {
      const response = await api.get(`/api/groups/${groupId}/members`);
      return { success: true, data: response.data?.data ?? [] };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Üyeler yüklenemedi', data: [] };
    }
  },

  // mode: 'active' (e-posta zorunlu, davet gider) | 'passive' (e-posta yok, owner yürütür)
  addMember: async (groupId, member) => {
    try {
      const response = await api.post(`/api/groups/${groupId}/members`, member);
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Üye eklenemedi' };
    }
  },

  // CSV istemcide ayrıştırılır; sunucuya satır listesi gider. Kısmi başarı normaldir —
  // yanıt hangi satırın neden geçmediğini söyler.
  bulkAddMembers: async (groupId, members) => {
    try {
      const response = await api.post(`/api/groups/${groupId}/members/bulk`, { members });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Toplu davet gönderilemedi' };
    }
  },

  updateBilling: async (groupId, billing) => {
    try {
      await api.put(`/api/groups/${groupId}/billing`, billing);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Fatura bilgisi kaydedilemedi' };
    }
  },

  // Uyeye randevu takibi tahsis eder / geri alir. Kota admin yetkisinden gelir ve sahibin TUM
  // gruplarinda sayilir; hak Notifications.Api'de bir bildirim paketine donusur.
  setMemberReservation: async (groupId, memberUserId, hasReservation) => {
    try {
      await api.put(
        `/api/groups/${groupId}/members/${memberUserId}/reservation`,
        { hasReservation },
      );
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Randevu takibi guncellenemedi' };
    }
  },

  // Üye bilgisi DÜZELTME — yanlış yazılmış ad ya da davet e-postası için.
  // Ülke / vize türü / meslek sunucuda KİLİTLİ (dağılım ve yüklenmiş belgeler onlara scoped);
  // ad ve e-posta yalnız kişi daveti kabul etmemişken (hasOwnAccount=false) düzenlenebilir.
  updateMember: async (groupId, memberUserId, changes) => {
    try {
      const response = await api.put(`/api/groups/${groupId}/members/${memberUserId}`, changes);
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Üye bilgisi güncellenemedi' };
    }
  },

  /**
   * Yöneticinin KENDİ başvurusu: kendi üyelik satırına ülke + vize türü + meslek yazar.
   * Sahibin satırı grup kurulurken ülkesiz açılıyor, bu yüzden hak listesi fiilen üyelerine
   * atadığı ülkelerden ibaret kalıyordu.
   */
  setOwnApplication: async (groupId, payload) => {
    try {
      const response = await api.put(`/api/groups/${groupId}/my-application`, payload);
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Başvurunuz kaydedilemedi' };
    }
  },

  removeMember: async (groupId, memberUserId) => {
    try {
      await api.delete(`/api/groups/${groupId}/members/${memberUserId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Üye çıkarılamadı' };
    }
  },

  // Yöneticinin ÜYE ADINA yüklediği belge — dağılımda ŞİRKETE atanmış türler için.
  //
  // Vekalet başlığı küresel bağlamdan DEĞİL burada tek seferlik veriliyor: bu, "üye adına
  // çalışma moduna geç" eylemi değil, grup ekranından yapılan tek bir yükleme. Sunucu türün o
  // üyenin dağılımında YÖNETİCİYE atandığını doğrular; atanmamışsa 403 döner (üyeden beklenen
  // belgeyi yönetici yükleyemez).
  uploadMemberDocument: async (memberUserId, countrySlug, documentType, file) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    if (countrySlug) formData.append('countrySlug', countrySlug);

    try {
      await api.post('/api/documents/upload-standard', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Acting-For': memberUserId },
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Belge yüklenemedi' };
    }
  },

  // Üyenin ilerlemesi. Vekalet başlığıyla çağrılır; yanıt bilerek fakirdir — analiz skoru yok.

  // Tüm üyelerin ilerlemesi TEK çağrıda. Yanıt bilerek fakir: analiz skoru yok, yalnız
  // "belgeler tamam mı", "analiz çalıştı mı" ve eksik belge adları.
  // Ozet HER UYENIN KENDI ulkesiyle hesaplanir; countrySlug artik yalniz istege bagli daraltma
  // (eski davranis: tek ulke herkese uygulaniyor, dagilimi olmayan ulkede herkes "tamam" cikiyordu).
  summary: async (groupId, countrySlug = null) => {
    try {
      const response = await api.get(`/api/documents/groups/${groupId}/summary`, {
        params: countrySlug ? { countrySlug } : {},
      });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Özet yüklenemedi' };
    }
  },

  remindMember: async (groupId, memberUserId, countrySlug) => {
    try {
      await api.post(
        `/api/documents/groups/${groupId}/members/${memberUserId}/remind`,
        null,
        { params: { countrySlug } },
      );
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Hatırlatma gönderilemedi' };
    }
  },

  // Belge TURU listesi — dagilim ekranlarinin TEK kaynagi.
  //
  // NEDEN countriesAPI.getStandardDocuments DEGIL: o uc odeme kapisindan geciyor ve sahibin
  // yetkisi kendi basvurusunu kapsamiyorsa TUM satirlari "Standart Belge (Kilitli)" olarak
  // donduruyor. On satirin adi ayni olunca ekran onlari ayirt edemiyor (radyo gruplari
  // cakisiyor, React key'leri tekrar ediyor) ve dagilim o maskeli adla KAYDEDILIYOR —
  // tamamlanma kapisi gercek belge adini aradigi icin matris hic eslesmiyordu.
  //
  // purpose + profession (uyenin vize turu / meslek segmenti, migration 067) verilirse amac/meslek
  // belgeleri de gelir (category: 'purpose'); yoksa yalniz standart (category: 'standard').
  documentTypes: async (groupId, countrySlug, { purpose = null, profession = null } = {}) => {
    try {
      const response = await api.get(
        `/api/countries/${countrySlug}/group-document-types`,
        { params: { groupId, purpose: purpose || undefined, profession: profession || undefined } },
      );
      return {
        success: true,
        data: response.data?.data?.documents ?? [],
        purposeCategory: response.data?.data?.purposeCategory ?? null,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Belge turleri yuklenemedi',
        data: [],
      };
    }
  },

  matrix: async (groupId, countrySlug) => {
    try {
      const response = await api.get(`/api/documents/groups/${groupId}/matrix`, { params: { countrySlug } });
      return { success: true, data: response.data?.data ?? [] };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Belge dağılımı yüklenemedi', data: [] };
    }
  },

  // Bir UYENIN yururlukteki dagilimi: grup varsayilani + o uyeye ozel override (migration 064).
  memberAssignments: async (groupId, memberUserId, countrySlug) => {
    try {
      const response = await api.get(
        `/api/documents/groups/${groupId}/members/${memberUserId}/assignments`,
        { params: { countrySlug } },
      );
      return { success: true, data: response.data?.data ?? [] };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Belge dagilimi yuklenemedi', data: [] };
    }
  },

  // UYE BASINA dagilim. Grup varsayilaniyla ayni olan secimler sunucuda satir ACMAZ: uye grup
  // ayarini izlemeye devam eder, override sadece gercekten farkli olanlar icin tutulur.
  saveMemberAssignments: async (groupId, memberUserId, countrySlug, assignments) => {
    try {
      await api.put(
        `/api/documents/groups/${groupId}/members/${memberUserId}/assignments`,
        { countrySlug, assignments },
      );
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Belge dagilimi kaydedilemedi' };
    }
  },

  saveMatrix: async (groupId, countrySlug, assignments) => {
    try {
      await api.put(`/api/documents/groups/${groupId}/matrix`, { countrySlug, assignments });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Belge dağılımı kaydedilemedi' };
    }
  },

  seats: async (groupId) => {
    try {
      const response = await api.get(`/api/payment-service/group-seats/${groupId}`);
      return { success: true, data: response.data?.data ?? { seats: [], pendingOrders: 0 } };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Koltuklar yüklenemedi',
        data: { seats: [], pendingOrders: 0 },
      };
    }
  },

  assignSeat: async (groupId, seatId, memberUserId, countrySlug) => {
    try {
      await api.post(`/api/payment-service/group-seats/${groupId}/${seatId}/assign`, {
        memberUserId, countrySlug,
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Koltuk atanamadı' };
    }
  },

  unassignSeat: async (groupId, seatId) => {
    try {
      await api.delete(`/api/payment-service/group-seats/${groupId}/${seatId}/assign`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Koltuk geri alınamadı' };
    }
  },

  buySeats: async ({ groupId, quantity, country, hasReservation, orderId }) => {
    try {
      const response = await api.post('/api/payment-service/payment-link/group-init', {
        groupId, quantity, country, hasReservation, orderId,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Koltuk alımı başlatılamadı' };
    }
  },

  // --- Üye tarafı: kendi bağım, iznim, ayrılma ---
  // Yönetici uçlarından ayrı: üye grubun üye listesini göremez, yalnız kendi bağını görür.

  myMemberships: async () => {
    try {
      const response = await api.get('/api/groups/my-memberships');
      return { success: true, data: response.data?.data ?? [] };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Grup bilgisi yüklenemedi', data: [] };
    }
  },

  // scope: 'full' (yönetici belgeleri görebilsin) | 'status' (varsayılan, yalnız ilerleme)
  setConsent: async (groupId, scope) => {
    try {
      await api.put(`/api/groups/${groupId}/consent`, { scope });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'İzin güncellenemedi' };
    }
  },

  leaveGroup: async (groupId) => {
    try {
      await api.post(`/api/groups/${groupId}/leave`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Gruptan ayrılınamadı' };
    }
  },

  // Ödeme dönüşü beklenmeden sekme kapanmışsa status hiç okunmaz; bu uç bekleyen siparişleri toparlar.
  // Üyeye ÜCRETSİZ analiz hakkı verir — admin'in tanımladığı grup yetkisi analizi de kapsıyor
  // (2026-08-19 ürün kararı), ayrı ödeme yok. Sunucu (üye, ülke) için idempotan davranır.

  syncSeats: async (groupId) => {
    try {
      const response = await api.post(`/api/payment-service/group-seats/sync/${groupId}`);
      return { success: true, data: response.data?.data };
    } catch {
      return { success: false };
    }
  },
};

/**
 * Grup daveti — oturum GEREKTİRMEZ. Daveti alan kişinin henüz kullanılabilir bir hesabı
 * olmayabilir (gölge hesap giriş yapamaz); yetki, e-postayla gelen tek kullanımlık token.
 */
export const groupInvitationAPI = {
  // requiresPassword false ise kişinin zaten hesabı var: şifre alanı GÖSTERİLMEZ ve
  // kabul mevcut şifreyi değiştirmez.
  preview: async (token) => {
    try {
      const response = await api.get(`/api/group-invitations/${encodeURIComponent(token)}`);
      return { valid: true, data: response.data?.data };
    } catch (error) {
      return { valid: false, error: error.response?.data?.error || 'Davet bağlantısı geçersiz ya da süresi dolmuş' };
    }
  },

  accept: async (token, password) => {
    try {
      const response = await api.post('/api/group-invitations/accept', { token, password });
      return { success: response.data?.success === true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Davet kabul edilemedi' };
    }
  },
};

export const retentionAPI = {
  resolveToken: async (token) => {
    try {
      const response = await api.get(`/api/retention/decision/${encodeURIComponent(token)}`);
      return response.data?.data ?? { valid: false, reason: 'invalid' };
    } catch {
      return { valid: false, reason: 'invalid' };
    }
  },

  submitDecision: async (token, decision) => {
    try {
      const response = await api.post('/api/retention/decision', { token, decision });
      return { success: response.data?.success === true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Seçiminiz kaydedilemedi' };
    }
  },

  // Hesap silme e-postasındaki geri alma bağlantısı. POST — tarayıcı önden çekip
  // kullanıcının gerçekten istediği silmeyi sessizce iptal etmesin.
  cancelDeletionByToken: async (token) => {
    try {
      const response = await api.post('/api/retention/deletion/cancel', { token });
      return { success: response.data?.success === true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Talep iptal edilemedi' };
    }
  },
};

// Welcome flow API (profile analysis & country recommendation)
export const welcomeAPI = {
  // Has the user already completed the welcome flow? -> { seen }
  getStatus: async () => {
    const response = await api.get('/api/welcome/status');
    return dataOf(response);
  },

  // Saved analysis, recomputed server-side -> { completed, skipped, recommendation, answers }
  getResult: async () => {
    const response = await api.get('/api/welcome/result');
    return dataOf(response);
  },

  // Mark welcome as seen without running recommendation (user pressed "Atla").
  skip: async () => {
    const response = await api.post('/api/welcome/skip');
    return dataOf(response);
  },

  // Visa rejection reasons for the dropdown -> { reasons: [{id, label}] } (or array)
  getRejectionReasons: async () => {
    const response = await api.get('/api/welcome/rejection-reasons');
    return dataOf(response);
  },

  // Compute recommendation without persisting -> { profile, profileLabel, countries }
  recommend: async (answers) => {
    const response = await api.post('/api/welcome/recommend', answers);
    return dataOf(response);
  },

  // Persist answers + recommendation -> { profile, profileLabel, countries }
  complete: async (answers) => {
    const response = await api.post('/api/welcome/complete', { answers });
    return dataOf(response);
  },
};

// Admin API
export const adminAPI = {
  // Grup yönetimi yetkisi (PLAN §K12, migration 060) — /grubum ekranını açan kapı.
  // Üye eklemek ücretsiz ama tavanlı: kaç üye eklenebileceğini admin belirler (memberLimit
  // zorunlu). Analiz hakkı ayrıca satın alınan koltuktan gelir.
  listGroupManagers: async () => {
    try {
      const response = await api.get('/api/admin/group-managers');
      return { success: true, data: response.data?.data || [] };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Yetki listesi alınamadı' };
    }
  },

  // memberLimit zorunlu (migration 060) — admin satılan pakete göre belirler.
  // reservationLimit: kac uyeye randevu takibi verilebilecegi (migration 063). Uye tavanindan
  // BAGIMSIZ ikinci sayac; 0 = randevusuz paket.
  // ownerIncluded: yetki sahibi KENDI basvurusu icin de kullanabilir mi (migration 065).
  // Varsayilan false — gonderilmezse sahip hak almaz (sessiz bedelsiz urun yerine gorunur kilit).
  // ownerIncluded varsayilani TRUE — sunucu varsayilaniyla ayni (migration 068): yetki =
  // odeme yapilmis, grup icindeki HERKESI kapsar. Panel degeri her zaman acikca gonderiyor;
  // varsayilan yalnizca baska bir cagiran alani atlarsa devreye girer ve o durumda sunucudan
  // FARKLI davranmasi sessiz bir tutarsizlik olurdu.
  grantGroupManager: async (userId, groupType, memberLimit, reservationLimit = 0, ownerIncluded = true) => {
    try {
      await api.post('/api/admin/group-managers', {
        userId, groupType, memberLimit, reservationLimit, ownerIncluded,
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Yetki verilemedi' };
    }
  },

  revokeGroupManager: async (userId) => {
    try {
      await api.delete(`/api/admin/group-managers/${userId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Yetki geri alınamadı' };
    }
  },

  getUsers: async () => {
    const response = await api.get('/api/admin/users');
    // V2: { data:[{id,username,email,phone,role,isActive,emailVerified,createdAt,lastLoginAt}], success }
    // (camelCase); AdminPage reads result.users and per-row SNAKE_CASE (user.is_active,
    // user.email_verified, user.last_login) for the status badge + activate/deactivate toggle +
    // Son Giriş column. Alias them here to keep AdminPage's existing snake_case convention.
    const body = response.data;
    return {
      success: body?.success === true,
      users: (body?.data || []).map((u) => ({
        ...u,
        // V2 .NET backend UserRole enum'unu PascalCase serialize ediyor ('User'/'Admin'), login
        // eşlemesi gibi burada da küçük harfe çeviriyoruz. Çevirmezsek satırdaki rol <select>'inin
        // hiçbir option'ı ('admin'/'user'/'test') eşleşmiyor ve tarayıcı ilk option'ı gösterip
        // HER kullanıcıyı "Admin" gibi gösteriyor; rol rengi de hiç 'admin'e denk gelmiyordu.
        role: u.role?.toLowerCase(),
        is_active: u.isActive,
        email_verified: u.emailVerified,
        last_login: u.lastLoginAt,
        contact_consent: Boolean(u.contactConsent),
      })),
    };
  },

  changeUserRole: async (userId, role) => {
    const response = await api.post(`/api/admin/users/${userId}/role`, { role });
    return response.data;
  },

  toggleUserStatus: async (userId, isActive) => {
    const response = await api.post(`/api/admin/users/${userId}/status`, { isActive });
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/api/admin/users/${userId}`);
    return response.data;
  },

  resendVerification: async (userId) => {
    const response = await api.post(`/api/admin/users/${userId}/resend-verification`);
    return response.data;
  },

  // --- KVKK saklama paneli ---

  getRetentionOverview: async (state = '') => {
    try {
      const query = state ? `?state=${encodeURIComponent(state)}` : '';
      const response = await api.get(`/api/admin/retention${query}`);
      return response.data?.data ?? { counts: {}, rows: [] };
    } catch {
      return { counts: {}, rows: [] };
    }
  },

  restoreRetention: async (userId) => {
    try {
      const response = await api.post(`/api/admin/users/${userId}/retention-restore`);
      return { success: response.data?.success === true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'İşlem başarısız' };
    }
  },

  sendRetentionNotice: async (userId) => {
    try {
      const response = await api.post(`/api/admin/users/${userId}/retention-notice`);
      return { success: response.data?.success === true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Bildirim gönderilemedi' };
    }
  },

  getSystemStats: async () => {
    const response = await api.get('/api/admin/stats');
    // V2: { data:{totalUsers,adminUsers}, success }; AdminPage reads stats.users.{TotalUsers,AdminUsers}.
    const d = response.data?.data || {};
    return { success: response.data?.success === true, stats: { users: { TotalUsers: d.totalUsers, AdminUsers: d.adminUsers } } };
  },

  getCountrySelections: async () => {
    const response = await api.get('/api/admin/country-selections');
    // V2: { data:[{userId,username,...camelCase}], success }; AdminPage reads result.selections
    // (each field already camelCase — matches the consumer).
    const body = response.data;
    return { success: body?.success === true, selections: body?.data || [] };
  },

  updateCountrySelections: async (userId, data) => {
    const response = await api.put(`/api/admin/country-selections/${userId}`, data);
    return response.data;
  },

  resetCountrySelections: async (userId) => {
    const response = await api.delete(`/api/admin/country-selections/${userId}`);
    return response.data;
  },

  // --- Ülke Değiştir (Kullanıcılar sekmesi) ---
  // Kullanıcının ülke başına belge + analiz durumu; taşımadan önce "neyi taşıyorum" ekranı.
  getUserDocumentCountries: async (userId) => {
    const response = await api.get(`/api/admin/document-country/${userId}`);
    const body = response.data;
    return { success: body?.success === true, countries: body?.data || [] };
  },

  // Belgeleri + analiz kaydını hedef ülkeye taşır. Ödemenin ülkesi (erişim kapısı) BURADA
  // değişmez — onu paymentAPI.assignPaymentCountry yapar, modal ikisini sırayla çağırır.
  transferUserDocumentCountry: async ({ userId, fromCountrySlug, toCountrySlug, overwriteConflicts = false }) => {
    const response = await api.post('/api/admin/document-country/transfer', {
      userId, fromCountrySlug, toCountrySlug, overwriteConflicts,
    });
    const body = response.data;
    return { success: body?.success === true, result: body?.data || null, error: body?.error || null };
  },

  // --- Kategori kilidi (Belge Analizi Ülkesi bölümü) ---
  // "Bir satın alım = bir ülke + BİR vize kategorisi" (migration 069). Kilit kullanıcının ilk
  // premium belge isteğinde kendiliğinden kurulur; ikinci kategori ya da yanlış seçim düzeltmesi
  // buradan geçer. Uç Catalog'da ve /api/countries önekinde: /api/admin gateway'de Admin.Api'ye
  // giden bir catch-all, oraya konsa 404 dönerdi.
  getUserCategoryLocks: async (userId) => {
    const response = await api.get(`/api/countries/category-locks/${userId}`);
    const body = response.data;
    return { success: body?.success === true, locks: body?.data || [] };
  },

  setUserCategoryLock: async (userId, countrySlug, purpose, profession) => {
    const response = await api.put(`/api/countries/category-locks/${userId}/${countrySlug}`, { purpose, profession });
    const body = response.data;
    return { success: body?.success === true, lock: body?.data || null, error: body?.error || null };
  },

  clearUserCategoryLock: async (userId, countrySlug) => {
    const response = await api.delete(`/api/countries/category-locks/${userId}/${countrySlug}`);
    const body = response.data;
    return { success: body?.success === true, error: body?.error || null };
  },

  // Admin: manually extend (or grant) a user's appointment-notification window by N days.
  extendNotificationPackage: async (userId, days) => {
    // V2 DTO is ExtendPackageRequestDto { UserId, Days } — snake_case user_id won't bind (→ Guid.Empty).
    const response = await api.post('/api/admin/notification-package/extend', { userId, days });
    return response.data;
  },

  exportUsers: async () => {
    const response = await api.get('/api/admin/users/export', { responseType: 'blob' });
    const contentDisposition = response.headers['content-disposition'] || '';
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : 'kullanicilar.xlsx';
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },

  getVisaCenters: async () => {
    const response = await api.get('/api/admin/visa-centers');
    return response.data;
  },

  updateVisaCenters: async (data) => {
    const response = await api.put('/api/admin/visa-centers', data);
    return response.data;
  },

  // Country onboarding (pre-appointment instructions) admin CRUD
  getCountryOnboarding: async () => {
    const response = await api.get('/api/admin/country-onboarding');
    // V2: { data:[{countryCode,countryName,flowType,steps,isActive,updatedAt}], success } (camelCase);
    // CountryOnboardingAdmin reads res.data[] with snake_case keys. Remap each row.
    const body = response.data;
    return {
      success: body?.success === true,
      data: (body?.data || []).map((r) => ({
        country_code: r.countryCode,
        country_name: r.countryName,
        flow_type: r.flowType,
        is_active: r.isActive,
        steps: r.steps,
        updated_at: r.updatedAt,
      })),
    };
  },

  createCountryOnboarding: async (payload) => {
    // Admin UI sends snake_case {country_code,country_name,flow_type,is_active,steps};
    // V2 CreateOnboardingRequestDto is camelCase {CountryCode,CountryName,FlowType,IsActive,Steps}.
    const response = await api.post('/api/admin/country-onboarding', {
      countryCode: payload.country_code,
      countryName: payload.country_name,
      flowType: payload.flow_type,
      isActive: payload.is_active,
      steps: payload.steps,
    });
    return response.data;
  },

  updateCountryOnboarding: async (countryCode, payload) => {
    // Request: snake_case → camelCase (UpdateOnboardingRequestDto). Response: camelCase data →
    // snake_case, because the admin UI does rowToDraft(res.data) expecting snake_case keys.
    const response = await api.put(`/api/admin/country-onboarding/${countryCode}`, {
      countryName: payload.country_name,
      flowType: payload.flow_type,
      isActive: payload.is_active,
      steps: payload.steps,
    });
    const d = response.data?.data || {};
    return {
      success: response.data?.success === true,
      data: {
        country_code: d.countryCode,
        country_name: d.countryName,
        flow_type: d.flowType,
        is_active: d.isActive,
        steps: d.steps,
      },
    };
  },

  deleteCountryOnboarding: async (countryCode) => {
    const response = await api.delete(`/api/admin/country-onboarding/${countryCode}`);
    return response.data;
  },

  // Manual appointment notification (admin-triggered when Telegram source is missing/delayed)
  getManualNotificationCountries: async () => {
    const response = await api.get('/api/admin/manual-notification/countries');
    // ApiResponse envelope: { success, data: [{code,name,flag}] } — unwrap like getActiveCountries.
    const body = response.data;
    return { success: body?.success === true, countries: body?.data || [] };
  },

  previewManualNotification: async (payload) => {
    const response = await api.post('/api/admin/manual-notification/preview', payload);
    // ApiResponse<ManualNotificationPreviewDto> envelope — unwrap so callers read
    // .recipients/.count/.countryName/.flag directly instead of .data.*.
    return dataOf(response);
  },

  sendManualNotification: async (payload) => {
    const response = await api.post('/api/admin/manual-notification/send', payload);
    // ApiResponse<ManualNotificationSendResultDto> envelope — unwrap so callers read
    // .sent/.failed/.total directly instead of .data.*.
    return dataOf(response);
  },

  getNotifications: async () => {
    const response = await api.get('/api/admin/notifications');
    // V2: { data:[{id,type,message,userId,metadata,isRead,createdAt}], success } (camelCase);
    // ProfileLayout reads res.notifications and n.is_read (snake_case). Map is_read.
    const body = response.data;
    return {
      success: body?.success === true,
      notifications: (body?.data || []).map((n) => ({ ...n, is_read: n.isRead })),
    };
  },

  markNotificationsRead: async () => {
    const response = await api.patch('/api/admin/notifications/mark-read');
    return response.data;
  },
};

// Visa Tips API
export const visaAPI = {
  loadVisaTips: async () => {
    try {
      // For now, return default tips directly without trying to load from backend
      // This avoids the proxy error to localhost:8080
      return [
        { name: 'Pasaport', tips: 'Pasaportunuzun vize bitiş tarihinden sonra en az 90 gün geçerli olduğundan emin olun' },
        { name: 'Seyahat Sigortası', tips: 'Schengen vizaları için seyahat sigortası zorunludur' },
        { name: 'Maddi Gelir', tips: 'Mali durumunuzu kanıtlamak için güncel banka ekstreleri sunun' },
        { name: 'Otel Rezervasyonu', tips: 'Konaklamanız için rezervasyon onayı gereklidir' },
        { name: 'Uçak Bileti', tips: 'Gidiş-dönüş uçak bileti rezervasyonu yapın' },
        { name: 'Vize Başvuru Formu', tips: 'Tüm alanları eksiksiz ve doğru doldurun' },
        { name: 'Biyometrik Fotoğraf', tips: 'Fotoğraf son 6 ay içinde çekilmiş olmalı' },
        { name: 'Banka Hesap Dökümü', tips: 'Son 3 aylık hesap dökümünü bankadan mühürletirin' },
        { name: 'İş Belgesi', tips: 'İşverenden alınan resmi çalışma belgesi gerekli' },
        { name: 'Konaklama Belgesi', tips: 'Otel rezervasyonu veya davet mektubu sunun' }
      ];
    } catch (error) {
      console.error('Error loading visa tips:', error);
      return [
        { name: 'Pasaport', tips: 'Pasaportunuzun vize bitiş tarihinden sonra en az 90 gün geçerli olduğundan emin olun' },
        { name: 'Seyahat Sigortası', tips: 'Schengen vizaları için seyahat sigortası zorunludur' },
        { name: 'Maddi Gelir', tips: 'Mali durumunuzu kanıtlamak için güncel banka ekstreleri sunun' }
      ];
    }
  },
};

// Countries API
// Ortak düzleştirici. Ülke verisinin OKUNAN İKİ ucu da aynı gövdeyi döner:
// { data: { country: {id,name,slug,photoUrl,description,visaType},
//           documents: [{id,documentName,description,category,link}] }, success }.
// Her iki çağıran da FLAT legacy şekli okuyor: {success, country (ad string), description,
// visa_type, photo_url, documents:[{name,...}]}.
const flattenCountryData = (response) => {
  const body = response.data;
  if (!body || body.success !== true) {
    throw new Error(body?.error || 'Request failed');
  }
  const country = body.data?.country || {};
  const documents = (body.data?.documents || []).map((d) => ({
    ...d,
    name: d.documentName ?? d.name,
  }));
  return {
    success: true,
    country: country.name || '',
    description: country.description || '',
    visa_type: country.visaType || '',
    photo_url: country.photoUrl || '',
    documents,
  };
};

export const countriesAPI = {
  getActiveCountries: async () => {
    try {
      const response = await api.get('/api/countries/active');
      // V2 returns { data: [...countries], success } with camelCase fields and NO is_active;
      // MainPage reads { success, countries } and CountryCard/handleCountryClick read snake_case
      // (photo_url, visa_type, is_active). Remap each country to the legacy snake_case shape.
      // is_active is always true here — this is the /active endpoint, so every row is active.
      const body = response.data;
      const countries = (body?.data || []).map((c) => ({
        ...c,
        photo_url: c.photoUrl ?? c.photo_url,
        visa_type: c.visaType ?? c.visa_type,
        is_active: c.isActive ?? c.is_active ?? true,
      }));
      return { success: body?.success === true, countries };
    } catch (error) {
      console.error('Error fetching active countries:', error);
      throw error;
    }
  },

  // Herkese açık uç: yalnız bilgilendirme kategorileri (bugün Değer Beyanı/DoV süreci).
  // Premium ipuçları ve linkler BURADAN GELMEZ — belge kontrol listesi için kapılı uçları
  // (getStandardDocuments / getPurposeDocuments / getEducationDocuments) kullanın.
  getCountryData: async (countrySlug) => {
    try {
      const response = await api.get(`/api/countries/${countrySlug}/data`);
      return flattenCountryData(response);
    } catch (error) {
      console.error(`Error fetching country data for ${countrySlug}:`, error);
      throw error;
    }
  },

  // Yönetici uç: ülkenin TAM Knowledge Base'i (her kategori, ipucu + link). Sunucuda
  // [AdminAuthorize] ile korunuyor; admin olmayan oturum 403 alır. Yalnız düzenleyici içindir.
  getCountryKnowledgeBase: async (countrySlug) => {
    try {
      const response = await api.get(`/api/knowledge-base/country/${countrySlug}/data`);
      return flattenCountryData(response);
    } catch (error) {
      console.error(`Error fetching knowledge base for ${countrySlug}:`, error);
      throw error;
    }
  },

  getStandardDocuments: async (countrySlug) => {
    try {
      const response = await api.get(`/api/countries/${countrySlug}/standard-documents`);
      // V2: { data: { country, countrySlug, documents, hasPaid, categoryLock }, success }.
      // ProfilePage reads response.{success, documents, country, hasPaid, categoryLock} → dataOf
      // hoists data up. categoryLock = satın alımın kapsadığı kategori (migration 069) ya da null.
      return dataOf(response);
    } catch (error) {
      console.error(`Error fetching standard documents for ${countrySlug}:`, error);
      throw error;
    }
  },

  /**
   * Bu ülkede belgesi OLAN (amaç, meslek) çiftleri — kategori seçim kutularının kaynağı.
   * Kapsam eskiden frontend'de gömülü ülke listeleriydi ve her içerik yüklemesinde koda
   * dokunmak gerekiyordu; bkz. constants/visaCategories.js.
   *
   * Hata durumunda `null` döner (throw ETMEZ): kapsam gelmezse ekran seçim kutusunu kilitli
   * tutar, yanlışlıkla "hepsi açık" ya da "hepsi kapalı" gibi davranmaz.
   */
  getAvailableCategories: async (countrySlug) => {
    try {
      const response = await api.get(`/api/countries/${countrySlug}/available-categories`);
      // V2: { data: { countrySlug, available }, success } → dataOf üstü kaldırır.
      return dataOf(response)?.available ?? null;
    } catch (error) {
      console.error(`Error fetching available categories for ${countrySlug}:`, error);
      return null;
    }
  },

  getPurposeDocuments: async (countrySlug, purpose, profession) => {
    try {
      const response = await api.get(
        `/api/countries/${countrySlug}/purpose-documents?purpose=${encodeURIComponent(purpose)}&profession=${encodeURIComponent(profession)}`
      );
      // V2: { data: { country, countrySlug, category, documents, hasPaid, categoryLock,
      //   categoryAllowed }, success }. ProfilePage reads response.{success, documents, ...} →
      // dataOf hoists data up. categoryAllowed=false → istenen kategori satın alımın kapsamı
      // dışında: documents BOŞ gelir ama hasPaid true kalır (paywall değil kilit açıklaması).
      return dataOf(response);
    } catch (error) {
      console.error(`Error fetching purpose documents for ${countrySlug} (${purpose}/${profession}):`, error);
      throw error;
    }
  },

  // Kullanıcı ödeme sonrası KENDİ vize kategorisini seçer ("Başvurularım" ekranı). Satın alım
  // böylece (ülke + kategori) olarak bağlanır. Zorunlu değil, hızlandırıcı: çağrılmazsa kilit
  // ilk premium belge isteğinde kendiliğinden kurulur. Yürürlükte FARKLI bir kilit varsa uç 409
  // döner — kategori değişikliği admin işi.
  setMyCategoryLock: async (countrySlug, purpose, profession) => {
    const response = await api.put(`/api/countries/my-category-lock/${encodeURIComponent(countrySlug)}`, { purpose, profession });
    const body = response.data;
    return { success: body?.success === true, result: body?.data || null };
  },

  getEducationDocuments: async (countrySlug, educationType) => {
    try {
      const response = await api.get(`/api/countries/${countrySlug}/education-documents?educationType=${encodeURIComponent(educationType)}`);
      // V2: { data: { country, countrySlug, educationType, programCategory, standart, program,
      //   hasPaid, categoryLock, categoryAllowed }, success }.
      // Under-18-only docs are inline inside `standart` (each flagged `isMinorOnly`), not a
      // separate list — the client decides active/disabled from the applicant's birth date.
      // dataOf hoists data up so callers read res.{success, standart, program, hasPaid, ...}.
      return dataOf(response);
    } catch (error) {
      console.error(`Error fetching education documents for ${countrySlug} (${educationType}):`, error);
      throw error;
    }
  },

  updateCountryData: async (countrySlug, documents, countryInfo = null) => {
    try {
      // V2 UpdateCountryRequestDto: { CountrySlug, Documents:[{Id,DocumentName,Description,Category,Link,IsDeleted}],
      //   PhotoUrl?, Description?, VisaType? }. Docs use {id,name,description,category,link} → remap name→documentName.
      // countryInfo display fields (photo_url/description/visa_type) ARE persisted server-side now; `name` is
      // intentionally NOT updatable (payment/entitlement records key on Country.Name — a rename would orphan them).
      const payload = {
        countrySlug,
        documents: (documents || []).map((d) => ({
          id: d.id,
          documentName: d.name ?? d.documentName,
          description: d.description,
          category: d.category,
          link: d.link,
          isDeleted: d.isDeleted ?? false,
        })),
        photoUrl: countryInfo?.photo_url,
        description: countryInfo?.description,
        visaType: countryInfo?.visa_type,
      };

      const response = await api.post('/api/knowledge-base/update-country', payload);
      // Backend returns a bare { success, message }; the form reads response.operations.total +
      // response.country. Synthesize them so the success path doesn't throw on response.operations.
      const body = response.data;
      return {
        success: body?.success === true,
        operations: { total: payload.documents.length },
        country: countrySlug,
      };
    } catch (error) {
      console.error(`Error updating country data for ${countrySlug}:`, error);
      throw error;
    }
  },

  replaceCountryDataFromExcel: async (countrySlug, formData) => {
    try {
      // Caller builds FormData with field `excel`; V2 ReplaceCountryRequestDto binds `ExcelFile`.
      // Rebuild with the field name the DTO expects (countrySlug already matches).
      const fixed = new FormData();
      const file = formData.get('excelFile') || formData.get('excel');
      if (file) fixed.append('excelFile', file);
      fixed.append('countrySlug', formData.get('countrySlug') || countrySlug);
      const response = await api.post(`/api/knowledge-base/replace-country-from-excel`, fixed, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // ApiResponse<KbReplaceResultDto> envelope: { success, data: { documentCount } }.
      const body = response.data;
      return {
        success: body?.success === true,
        documentCount: body?.data?.documentCount,
        country: countrySlug,
      };
    } catch (error) {
      console.error(`Error replacing country data from Excel for ${countrySlug}:`, error);
      throw error;
    }
  },

  importCountry: async (formData) => {
    try {
      const response = await api.post('/api/knowledge-base/import-country', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // ApiResponse<KbImportResultDto> envelope: { success, data: {documentCount,name,slug,visaType} }
      // — unwrap so callers read those fields directly instead of .data.*.
      return dataOf(response);
    } catch (error) {
      console.error('Error importing country:', error);
      throw error;
    }
  },

  uploadCountryPhoto: async (countrySlug, photoFile) => {
    try {
      // V2 PhotosController binds a single `IFormFile file` (was `photo` → 400 "No file provided").
      const formData = new FormData();
      formData.append('file', photoFile);

      const response = await api.post('/api/admin/upload-country-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // V2 returns ApiResponse<string> { data:<url>, success }; the form reads response.{success, photoUrl}
      // (and response.storage for a message — absent in V2, so the generic "uploaded" message shows).
      const body = response.data;
      return { success: body?.success === true, photoUrl: body?.data };
    } catch (error) {
      console.error('Error uploading country photo:', error);
      throw error;
    }
  },

  deleteCountry: async (countrySlug) => {
    try {
      const response = await api.delete(`/api/knowledge-base/country/${countrySlug}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting country ${countrySlug}:`, error);
      throw error;
    }
  },
};

// Visa Appointment API - Vize randevu takip sistemi
export const visaAppointmentAPI = {
  // Tüm ülkelerin randevu durumlarını getir
  getAll: async () => {
    try {
      const response = await api.get('/api/visa-appointments');
      // V2: { data:[{countryCode,countryName,status,earliestDate,...}], success } (camelCase);
      // the dashboard reads { success, data } and renders each item snake_case. Keep the envelope
      // shape but alias each row's fields to the legacy snake_case keys the components read.
      const body = response.data;
      return {
        success: body?.success === true,
        data: (body?.data || []).map((a) => ({
          ...a,
          country_code: a.countryCode,
          country_name: a.countryName,
          earliest_date: a.earliestDate,
          appointment_type: a.appointmentType,
          booking_url: a.bookingUrl,
          data_source: a.dataSource,
          last_checked_at: a.lastCheckedAt,
          scrape_http_status: a.scrapeHttpStatus,
          scrape_error: a.scrapeError,
        })),
      };
    } catch (error) {
      console.error('Error fetching visa appointments:', error);
      throw error;
    }
  },

  // Tek ülkenin randevu durumunu getir
  getByCountry: async (countryCode) => {
    try {
      const response = await api.get(`/api/visa-appointments/${countryCode}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching visa appointment for ${countryCode}:`, error);
      throw error;
    }
  },

  // Tüm ülkeleri tara (Admin only)
  scanAll: async (countryCodes = null) => {
    try {
      const response = await api.post('/api/visa-appointments/scan', {
        countryCodes
      });
      return response.data;
    } catch (error) {
      console.error('Error scanning visa appointments:', error);
      throw error;
    }
  },

  // Tek ülkeyi tara (Admin only)
  scanCountry: async (countryCode) => {
    try {
      const response = await api.post(`/api/visa-appointments/scan/${countryCode}`);
      return response.data;
    } catch (error) {
      console.error(`Error scanning visa appointment for ${countryCode}:`, error);
      throw error;
    }
  },
};

// Telegram API - Telegram vize randevu bildirimleri
export const telegramAPI = {
  // Oturum durumunu kontrol et
  getAuthStatus: async () => {
    try {
      const response = await api.get('/api/telegram/auth/status');
      return response.data;
    } catch (error) {
      console.error('Error checking telegram auth status:', error);
      throw error;
    }
  },

  // Telefon numarasına kod gönder
  sendCode: async (phoneNumber) => {
    try {
      const response = await api.post('/api/telegram/auth/send-code', { phoneNumber });
      return response.data;
    } catch (error) {
      console.error('Error sending telegram code:', error);
      throw error;
    }
  },

  // Kodu doğrula
  verifyCode: async (phoneNumber, code, password = null) => {
    try {
      const response = await api.post('/api/telegram/auth/verify', { 
        phoneNumber, 
        code, 
        password 
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying telegram code:', error);
      throw error;
    }
  },

  // Kaynakları listele
  getSources: async () => {
    try {
      const response = await api.get('/api/telegram/sources');
      return response.data;
    } catch (error) {
      console.error('Error fetching telegram sources:', error);
      throw error;
    }
  },

  // Yeni kaynak ekle
  addSource: async (channelLink, startDate, filterKeywords = null) => {
    try {
      const response = await api.post('/api/telegram/sources', { 
        channelLink, 
        startDate,
        filterKeywords,
      });
      return response.data;
    } catch (error) {
      console.error('Error adding telegram source:', error);
      throw error;
    }
  },

  // Kaynak sil
  deleteSource: async (sourceId) => {
    try {
      const response = await api.delete(`/api/telegram/sources/${sourceId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting telegram source:', error);
      throw error;
    }
  },

  // Mesajları getir
  getMessages: async (limit = 50, offset = 0, sourceId = null) => {
    try {
      const params = { limit, offset };
      if (sourceId) params.sourceId = sourceId;
      const response = await api.get('/api/telegram/messages', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching telegram messages:', error);
      throw error;
    }
  },

  // Kaynaktan manuel mesaj çek
  fetchSource: async (sourceId) => {
    try {
      const response = await api.post(`/api/telegram/sources/${sourceId}/fetch`);
      return response.data;
    } catch (error) {
      console.error('Error fetching telegram source:', error);
      throw error;
    }
  },

  // Tüm kaynaklardan mesaj çek
  fetchAll: async () => {
    try {
      const response = await api.post('/api/telegram/fetch-all');
      return response.data;
    } catch (error) {
      console.error('Error fetching all telegram sources:', error);
      throw error;
    }
  },
};

// Visardbot Randevu Takip API
export const visardAppointmentAPI = {
  getCountryStatuses: async () => {
    try {
      const response = await api.get('/api/appointments/visardbot');
      // V2: { data:{lastUpdatedAt, countries:[...], selectionInfo:{...}}, success }; the page reads
      // flat response.{success, countries, selectionInfo}. Hoist data up. countries[] items already
      // match the camelCase keys the components read.
      const body = response.data;
      const d = body?.data || {};
      return {
        success: body?.success === true,
        countries: d.countries || [],
        selectionInfo: d.selectionInfo || null,
        lastUpdatedAt: d.lastUpdatedAt,
      };
    } catch (error) {
      if (error.response && error.response.status === 403) {
        // V2 signals "reservation required" via code, not a requiresReservation flag.
        return {
          success: false,
          requiresReservation: error.response.data?.code === 'RESERVATION_REQUIRED'
            || error.response.data?.requiresReservation || false,
          error: error.response.data?.error,
        };
      }
      console.error('Error fetching visardbot appointments:', error);
      throw error;
    }
  },
};

// Country Selection API
export const countrySelectionAPI = {
  getSelections: async () => {
    try {
      const response = await api.get('/api/user/country-selections');
      return response.data;
    } catch (error) {
      console.error('Error fetching country selections:', error);
      throw error;
    }
  },

  // `centers` may be a string (legacy single-center) or string[] (multi-select,
  // used for Istanbul sub-centers Altunizade/Beyoğlu/Harbiye). The backend
  // accepts both via the `center` / `centers` payload keys; we normalise here.
  // `visaTypes` is string[] of canonical category labels (constants/visaTypes.jsx);
  // omitted/empty = all categories.
  //
  // `visaCategories` is what the user actually SAW and picked in ADIM 4 ("Turizm (İlk
  // başvurusu olanlar için)"). Yalnız kayıt için gider — bildirim eşleştirmesi `visaTypes`
  // ile yapılır. İkisi birden gönderilir çünkü kaba etiket "kullanıcı ne seçti" sorusunu
  // cevaplayamıyor: admin ve destek ekranları ince satırı görmeden bildirimi açıklayamıyordu.
  addSelection: async (countryCode, centers, visaTypes, visaCategories) => {
    try {
      const body = { countryCode };
      if (Array.isArray(centers)) {
        body.centers = centers;
      } else if (centers) {
        body.center = centers;
      }
      if (Array.isArray(visaTypes) && visaTypes.length > 0) {
        body.visaTypes = visaTypes;
      }
      if (Array.isArray(visaCategories) && visaCategories.length > 0) {
        body.visaCategories = visaCategories;
      }
      const response = await api.post('/api/user/country-selections', body);
      // V2: { data:{message,count,maxSelections,isLocked}, success }; TrackPage reads flat
      // result.{success, isLocked, error}. Hoist data up.
      const resBody = response.data;
      return { success: resBody?.success === true, ...(resBody?.data || {}) };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error,
          requiresReservation: error.response.data?.code === 'RESERVATION_REQUIRED'
            || error.response.data?.requiresReservation || false,
        };
      }
      console.error('Error adding country selection:', error);
      throw error;
    }
  },

  removeSelection: async (countryCode) => {
    try {
      const response = await api.delete(`/api/user/country-selections/${countryCode}`);
      // V2: { data:{message,count,maxSelections,isLocked}, success }; TrackPage reads result.{success, error}.
      const resBody = response.data;
      return { success: resBody?.success === true, ...(resBody?.data || {}) };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error,
        };
      }
      console.error('Error removing country selection:', error);
      throw error;
    }
  },

  updateNotificationPreferences: async ({ channel, phone }) => {
    try {
      const body = { channel };
      if (phone !== undefined) body.phone = phone;
      const response = await api.patch('/api/user/notification-preferences', body);
      // V2: { data:{channel,phone}, success }; TrackPage reads result.{success, notificationPrefs:{channel,phone}}.
      const resBody = response.data;
      return { success: resBody?.success === true, notificationPrefs: resBody?.data || null };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error,
        };
      }
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  },
};

// Cover Letter API
export const coverLetterAPI = {
  // educationType is optional (Eğitim/D-tipi flow only, e.g. 'lisans'/'yuksek'/'erasmus'/
  // 'erasmusStaj' — same canonical values as the Documents flow's selectedEducationType).
  // countrySlug is optional (issue #2 Phase B) — lets the backend fill gaps in the letter
  // from document-analysis extraction (see documentsAPI.getAnalysisStatus, same slug value).
  generate: async ({ visaType, destinationCountry, additionalDetails, educationType, countrySlug }) => {
    try {
      const response = await api.post('/api/cover-letter/generate', {
        visaType,
        destinationCountry,
        additionalDetails,
        ...(educationType && { educationType }),
        ...(countrySlug && { countrySlug }),
      });
      // Python cover-letter-api returns a FLAT snake_case dict (not the {data,success} envelope):
      // { success, cover_letter, needs_more_info, missing_fields, message, error }.
      // CoverLetterPage reads result.{success, coverLetter, needsMoreInfo, message, error}.
      const d = response.data || {};
      return {
        success: d.success,
        coverLetter: d.cover_letter,
        needsMoreInfo: d.needs_more_info,
        missingFields: d.missing_fields,
        message: d.message,
        error: d.error,
      };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || 'Cover letter generation failed',
        };
      }
      console.error('Error generating cover letter:', error);
      throw error;
    }
  },

  // issue #2 Phase B — merged profile + document-extracted known facts, shaped to this
  // page's own form field names. { success, fields, sources, conflicts }.
  getPrefillData: async (countrySlug) => {
    try {
      // Python cover-letter-api's endpoint takes a plain (non-aliased) FastAPI query param
      // named country_slug — unlike the C#-backed /draft endpoint above, this one needs the
      // snake_case name verbatim, not countrySlug.
      const response = await api.get('/api/cover-letter/prefill', { params: { country_slug: countrySlug } });
      return response.data;
    } catch (error) {
      console.error('Error getting cover letter prefill data:', error);
      return { success: false, fields: {}, sources: {}, conflicts: {} };
    }
  },

  getTravelSuggestion: async (country, nights) => {
    try {
      const response = await api.post('/api/cover-letter/travel-suggestion', { country, nights });
      return response.data;
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || 'Travel suggestion failed',
        };
      }
      console.error('Error getting travel suggestion:', error);
      throw error;
    }
  },

  saveDraft: async (countrySlug, letterText) => {
    try {
      const response = await api.post('/api/cover-letter/draft', { countrySlug, letterText });
      return response.data;
    } catch (error) {
      console.error('Error saving cover letter draft:', error);
      throw error;
    }
  },

  getDraft: async (countrySlug) => {
    try {
      const response = await api.get('/api/cover-letter/draft', { params: { countrySlug } });
      const body = response.data;
      // V2 identity-api returns the standard ApiResponse envelope { success, data: {text, savedAt}|null }.
      // (Legacy Node returned { success, draft } — accept either so the page's res.draft.text still works.)
      const draft = body?.data ?? body?.draft ?? null;
      return { success: body?.success ?? true, draft };
    } catch (error) {
      console.error('Error getting cover letter draft:', error);
      return { success: false, draft: null };
    }
  },
};

// Visa Centers API (City-Based Reservation)
export const visaCentersAPI = {
  getCenters: async () => {
    try {
      const response = await api.get('/api/visa-centers');
      return response.data;
    } catch (error) {
      console.error('Error fetching visa centers:', error);
      throw error;
    }
  },

  saveResidenceCity: async (city) => {
    try {
      const response = await api.put('/api/user/residence-city', { city });
      return response.data;
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error,
        };
      }
      console.error('Error saving residence city:', error);
      throw error;
    }
  },
};

// Pre-Appointment Instructions API (shown in a warning modal before country selection)
export const preAppointmentInstructionsAPI = {
  getAll: async () => {
    try {
      const response = await api.get('/api/countries/pre-appointment-instructions');
      return response.data;
    } catch (error) {
      console.error('Error fetching pre-appointment instructions:', error);
      throw error;
    }
  },
};

// Web Push subscription API (Chrome bildirimleri — randevu takibi)
// Sohbet soru-cevap havuzu (chat_qa, migration 071). Tüm uçlar admin: buraya yazılan metin
// doğrudan modelin bağlamına giriyor.
export const chatQaAPI = {
  // country verilmezse TÜM kapsamlar döner (ülkeler-üstü + varsa ülkeye özel).
  list: async ({ country, includeInactive = true } = {}) => {
    try {
      const params = new URLSearchParams();
      if (country) params.set('country', country);
      params.set('includeInactive', String(includeInactive));
      const response = await api.get(`/api/chat-qa?${params.toString()}`);
      // ApiResponse<List<ChatQaItemDto>> — dizi döner, dataOf onu olduğu gibi verir.
      return dataOf(response) || [];
    } catch (error) {
      console.error('Error listing chat Q&A:', error);
      throw error;
    }
  },

  // Excel'den toplu değiştirme. Bozuk/boş dosyada backend HİÇBİR ŞEY silmez, 400 döner.
  replaceFromExcel: async (file, { country } = {}) => {
    try {
      const form = new FormData();
      form.append('excelFile', file);
      if (country) form.append('country', country);
      const response = await api.post('/api/chat-qa/replace-from-excel', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return dataOf(response);
    } catch (error) {
      console.error('Error replacing chat Q&A from Excel:', error);
      throw error;
    }
  },

  // Ekrandan elle düzenleme — Excel yolunun aynı değiştirme semantiği.
  save: async (items, { country } = {}) => {
    try {
      const response = await api.post('/api/chat-qa/save', { items, country: country || null });
      return dataOf(response);
    } catch (error) {
      console.error('Error saving chat Q&A:', error);
      throw error;
    }
  },
};

export const pushAPI = {
  getVapidPublicKey: async () => {
    try {
      const response = await api.get('/api/user/push-subscriptions/vapid-public-key');
      return response.data?.data?.publicKey;
    } catch (error) {
      console.error('Error fetching VAPID public key:', error);
      throw error;
    }
  },

  saveSubscription: async ({ endpoint, p256dh, auth, userAgent }) => {
    try {
      const response = await api.post('/api/user/push-subscriptions', {
        endpoint,
        p256dh,
        auth,
        userAgent,
      });
      return response.data;
    } catch (error) {
      console.error('Error saving push subscription:', error);
      throw error;
    }
  },

  deleteSubscription: async (endpoint) => {
    try {
      const response = await api.delete(
        `/api/user/push-subscriptions?endpoint=${encodeURIComponent(endpoint)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting push subscription:', error);
      throw error;
    }
  },
};

export default api;